import React, { useState, useEffect, useRef } from 'react';
import { apiBaseUrl } from '../config.js';
import logoMain from '../img/inmoapp_logo.png';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

let ioInstance = null;

const Metrics = ({ userToken, section = 'platform', onMobileChatOpenChange }) => {
    // Get today's date in YYYY-MM-DD format (Argentina timezone)
    const getTodayDate = () => {
        // Get current date in Argentina timezone (UTC-3)
        const now = new Date();
        const argentinaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
        const year = argentinaTime.getFullYear();
        const month = String(argentinaTime.getMonth() + 1).padStart(2, '0');
        const day = String(argentinaTime.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const getTomorrowDate = () => {
        // Get tomorrow's date in Argentina timezone (UTC-3)
        const now = new Date();
        const argentinaTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/Argentina/Buenos_Aires' }));
        argentinaTime.setDate(argentinaTime.getDate() + 1);
        const year = argentinaTime.getFullYear();
        const month = String(argentinaTime.getMonth() + 1).padStart(2, '0');
        const day = String(argentinaTime.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [platformMetrics, setPlatformMetrics] = useState(null);
    const [funnelMetrics, setFunnelMetrics] = useState(null);
    const [tasaciones, setTasaciones] = useState([]);
    const [tasacionesPagination, setTasacionesPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [addressSearch, setAddressSearch] = useState('');
    const [selectedTasacionDetail, setSelectedTasacionDetail] = useState(null);
    const [showTasacionModal, setShowTasacionModal] = useState(false);
    const [activeUsers, setActiveUsers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sectionLoading, setSectionLoading] = useState(false);
    const [loadingSection, setLoadingSection] = useState(section);
    // Use section prop instead of activeTab state
    const activeTab = section;
    // Default startDate to today, endDate to tomorrow (+1 day)
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTomorrowDate());
    const [realtimeNotification, setRealtimeNotification] = useState(null);
    const [leadSources, setLeadSources] = useState(null);
    const [conversationsCount, setConversationsCount] = useState(0);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);
    const [selectedTasaciones, setSelectedTasaciones] = useState([]);
    
    // Appointment States
    const [appointmentDate, setAppointmentDate] = useState('');
    const [appointmentTime, setAppointmentTime] = useState('');
    const [appointmentName, setAppointmentName] = useState('');
    const [savingAppointment, setSavingAppointment] = useState(false);
    
    // WhatsApp Messaging States
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [conversationsLoading, setConversationsLoading] = useState(false);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [templates, setTemplates] = useState([]);
    const [showTemplates, setShowTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateParameters, setTemplateParameters] = useState([]);
    const [newMessageNotification, setNewMessageNotification] = useState(null);
    const [isMobile, setIsMobile] = useState(false);
    const [showMobileConversation, setShowMobileConversation] = useState(false);
    const [isSocketConnected, setIsSocketConnected] = useState(false);
    
    // Ref for auto-scrolling messages
    const messagesEndRef = useRef(null);
    
    // Settings state
    const [whatsappVerificationEnabled, setWhatsappVerificationEnabled] = useState(true);
    const [autoSendWhatsAppTemplate, setAutoSendWhatsAppTemplate] = useState(false);
    const [requirePhoneNumber, setRequirePhoneNumber] = useState(true);
    const [sendTasacionCompletionEmails, setSendTasacionCompletionEmails] = useState(true);
    const [enablePayOption, setEnablePayOption] = useState(false);
    const [payOptionPrice, setPayOptionPrice] = useState(1000);
    const [payOptionDiscountPercent, setPayOptionDiscountPercent] = useState(0);
    const [enableFreeTasacion, setEnableFreeTasacion] = useState(true);
    const [settingsLoading, setSettingsLoading] = useState(false);
    const [sendingPendingWhatsApp, setSendingPendingWhatsApp] = useState(false);

    // Update loadingSection when section changes
    useEffect(() => {
        setLoadingSection(section);
    }, [section]);

    // Mobile detection
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            // Reset mobile conversation view when switching to desktop
            if (!mobile && showMobileConversation) {
                setShowMobileConversation(false);
                if (onMobileChatOpenChange) {
                    onMobileChatOpenChange(false);
                }
            }
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, [showMobileConversation, onMobileChatOpenChange]);
    
    // Prevent body scroll on mobile messages page
    useEffect(() => {
        if (isMobile && activeTab === 'messages') {
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.height = '100%';
        } else {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        }
        
        return () => {
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.height = '';
        };
    }, [isMobile, activeTab]);
    
    // Reset mobile conversation view when switching tabs
    useEffect(() => {
        if (showMobileConversation) {
            setShowMobileConversation(false);
            if (onMobileChatOpenChange) {
                onMobileChatOpenChange(false);
            }
        }
    }, [activeTab]);
    
    // Auto-scroll when messages change (only on new messages)
    useEffect(() => {
        if (messages.length > 0 && selectedConversation) {
            // Delay to ensure DOM is updated
            setTimeout(() => scrollToBottom(), 50);
        }
    }, [messages.length, selectedConversation]);

    useEffect(() => {
        if (activeTab === 'settings' && userToken) {
            fetchSettings();
        }
        if (activeTab === 'messages' && userToken) {
            fetchTemplates();
        }
    }, [activeTab, userToken]);

    useEffect(() => {
        if (userToken) {
            fetchMetrics(1);
            setupSocket();
            const poll = setInterval(fetchRealtimeFallback, 15000);
            return () => {
                clearInterval(poll);
                if (ioInstance) {
                    ioInstance.off('connect');
                    ioInstance.off('disconnect');
                    ioInstance.off('active_users');
                    ioInstance.off('new_tasacion');
                    ioInstance.off('new_whatsapp_message');
                }
            };
        }
    }, [userToken]);
    
    // Note: We removed the auto-refetch on date change to avoid duplicate fetches
    // Users need to click "Filtrar" button to apply date changes

    const setupSocket = async () => {
        try {
            if (!ioInstance) {
                const { io } = await import('socket.io-client');
                // Pass admin token in connection auth
                ioInstance = io(apiBaseUrl, { 
                    transports: ['websocket'], 
                    withCredentials: false,
                    auth: {
                        token: userToken,
                        isAdmin: true
                    }
                });
            }
            
            // Listen for connection status
            ioInstance.on('connect', () => {
                console.log('✅ Socket.IO connected');
                setIsSocketConnected(true);
            });
            
            ioInstance.on('disconnect', () => {
                console.log('❌ Socket.IO disconnected');
                setIsSocketConnected(false);
            });
            
            // Check if socket is already connected (important for navigation between pages)
            if (ioInstance.connected) {
                console.log('✅ Socket was already connected');
                setIsSocketConnected(true);
            }
            
            ioInstance.on('active_users', (count) => {
                setActiveUsers(count);
            });
            
            ioInstance.on('new_tasacion', (data) => {
                setRealtimeNotification({
                    message: `Nueva tasación: ${data.zone || 'N/A'} - ${data.type || 'N/A'}`,
                    timestamp: data.timestamp
                });
                
                // Auto-refresh metrics (reset to page 1)
                fetchMetrics(1);
                
                // Clear notification after 5 seconds
                setTimeout(() => setRealtimeNotification(null), 5000);
            });
            
            // Listen for new WhatsApp messages
            ioInstance.on('new_whatsapp_message', (data) => {
                console.log('📱 New WhatsApp message received via Socket.IO:', data);
                
                // Update conversations list - use callback to avoid stale state
                setConversations(prevConversations => {
                    // Find if conversation exists
                    const existingConv = prevConversations.find(c => c.phoneNumber === data.phone);
                    
                    if (existingConv) {
                        // Update existing conversation - preserve address and other tasación data
                        return prevConversations.map(conv => 
                            conv.phoneNumber === data.phone 
                                ? { 
                                    ...conv, 
                                    lastMessage: data.message, 
                                    lastMessageTime: data.timestamp,
                                    // Don't increment unread if it's the selected conversation or if outgoing
                                    unreadCount: selectedConversation?.phoneNumber === data.phone || data.direction === 'outgoing' ? 0 : (conv.unreadCount || 0) + 1,
                                    // Preserve address, property, and zone from tasación
                                    address: conv.address,
                                    property: conv.property,
                                    zone: conv.zone
                                }
                                : conv
                        ).sort((a, b) => new Date(b.lastMessageTime) - new Date(a.lastMessageTime));
                    } else {
                        // New conversation - refresh from server
                        fetchConversations();
                        return prevConversations;
                    }
                });
                
                // If viewing this conversation, append message instead of refreshing
                setSelectedConversation(prevSelected => {
                    if (prevSelected && prevSelected.phoneNumber === data.phone) {
                        // Append new message to messages array
                        setMessages(prevMessages => {
                            // Check if message already exists (avoid duplicates)
                            const messageExists = prevMessages.some(msg => 
                                msg.content === data.message && 
                                Math.abs(new Date(msg.timestamp) - new Date(data.timestamp)) < 1000
                            );
                            
                            if (messageExists) {
                                return prevMessages;
                            }
                            
                            // Add new message - use real messageId from backend if available
                            const newMessage = {
                                messageId: data.messageId || `${data.phone}-${Date.now()}`, // Use real Twilio messageId
                                _id: data._id || null, // Use MongoDB _id if available
                                conversationPhone: data.phone,
                                content: data.message,
                                direction: data.direction || 'incoming',
                                timestamp: data.timestamp,
                                status: 'received',
                                type: data.type || 'text',
                                mediaUrl: data.mediaUrl || null,
                                mediaType: data.mediaType || null
                            };
                            
                            // DEBUG: Log new message structure
                            if (newMessage.mediaUrl) {
                                console.log('📩 Adding real-time media message:', {
                                    messageId: newMessage.messageId,
                                    type: newMessage.type,
                                    mediaType: newMessage.mediaType,
                                    hasMediaUrl: !!newMessage.mediaUrl
                                });
                            }
                            
                            return [...prevMessages, newMessage];
                        });
                        
                        // Scroll to bottom after message is added
                        setTimeout(() => scrollToBottom(), 100);
                    } else if (data.direction === 'incoming') {
                        // New message for a different conversation - show notification
                        setNewMessageNotification({
                            phone: data.phone,
                            message: data.message,
                            timestamp: data.timestamp
                        });
                        setTimeout(() => setNewMessageNotification(null), 5000);
                    }
                    return prevSelected;
                });
            });
        } catch (e) {
        }
    };

    const fetchRealtimeFallback = async () => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/stats/realtime`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (typeof data.activeUsers === 'number') setActiveUsers(data.activeUsers);
            }
        } catch (_) {}
    };

    const fetchAppointments = async () => {
        setAppointmentsLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/martillero-appointments?limit=100`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setAppointments(data.appointments);
                }
            }
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setAppointmentsLoading(false);
        }
    };

    // Fetch appointments when tab changes to appointments
    useEffect(() => {
        if (activeTab === 'appointments' && userToken) {
            fetchAppointments();
        }
        // Fetch conversations when tab changes to messages
        if (activeTab === 'messages' && userToken) {
            fetchConversations();
        }
        // Clear selected tasaciones when switching tabs
        if (activeTab !== 'tasaciones') {
            setSelectedTasaciones([]);
        }
    }, [activeTab, userToken]);

    // Handle mobile browser visibility (when user returns to the browser)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (!document.hidden && activeTab === 'messages' && userToken) {
                console.log('📱 Browser became visible - refreshing messages');
                
                // Reconnect socket if disconnected
                if (ioInstance && !ioInstance.connected) {
                    console.log('🔄 Reconnecting socket...');
                    ioInstance.connect();
                }
                
                // Refresh conversations
                fetchConversations();
                
                // If a conversation is selected, refresh its messages
                if (selectedConversation) {
                    fetchMessages(selectedConversation.phoneNumber);
                    // Scroll to bottom after messages refresh
                    setTimeout(() => scrollToBottom(), 400);
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [activeTab, userToken, selectedConversation]);

    const updateAppointmentStatus = async (appointmentId, newStatus) => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            await fetch(`${apiBaseUrl}/api/admin/martillero-appointments/${appointmentId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ status: newStatus })
            });
            
            // Update local state
            setAppointments(prev => prev.map(apt => 
                apt._id === appointmentId ? { ...apt, status: newStatus } : apt
            ));
        } catch (err) {
            console.error('Error updating appointment status:', err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const toggleTasacionSelection = (tasacion) => {
        setSelectedTasaciones(prev => {
            const isSelected = prev.some(t => t._id === tasacion._id);
            if (isSelected) {
                return prev.filter(t => t._id !== tasacion._id);
            } else {
                return [...prev, tasacion];
            }
        });
    };

    const toggleSelectAll = () => {
        if (selectedTasaciones.length === tasaciones.length) {
            setSelectedTasaciones([]);
        } else {
            setSelectedTasaciones([...tasaciones]);
        }
    };

    // WhatsApp Messaging Functions
    const fetchTemplates = async () => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const response = await fetch(`${apiBaseUrl}/api/whatsapp/templates`, { headers });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setTemplates(data.templates || []);
                }
            }
        } catch (err) {
            console.error('Error fetching templates:', err);
        }
    };
    
    const fetchConversations = async () => {
        setConversationsLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const searchParam = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
            const response = await fetch(`${apiBaseUrl}/api/whatsapp/conversations${searchParam}`, { headers });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setConversations(data.conversations || []);
                }
            }
        } catch (err) {
            console.error('Error fetching conversations:', err);
        } finally {
            setConversationsLoading(false);
        }
    };

    const fetchMessages = async (phoneNumber) => {
        setMessagesLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const response = await fetch(`${apiBaseUrl}/api/whatsapp/messages/${phoneNumber}`, { headers });
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setMessages(data.messages || []);
                }
            }
        } catch (err) {
            console.error('Error fetching messages:', err);
        } finally {
            setMessagesLoading(false);
        }
    };

    const sendMessage = async () => {
        if (!selectedConversation) return;
        if (!selectedTemplate && !messageInput.trim()) return;

        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            
            const payload = {
                phone: selectedConversation.phoneNumber
            };
            
            if (selectedTemplate) {
                // Sending template message
                payload.isTemplate = true;
                payload.templateName = selectedTemplate.name;
                payload.templateLanguage = selectedTemplate.language;
                payload.templateParameters = templateParameters;
            } else {
                // Sending regular text message
                payload.isTemplate = false;
                payload.message = messageInput;
            }
            
            const response = await fetch(`${apiBaseUrl}/api/whatsapp/send-message`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    // Add message to local state
                    const messageContent = selectedTemplate 
                        ? `[Template: ${selectedTemplate.name}]` 
                        : messageInput;
                    
                    setMessages([...messages, {
                        _id: data.messageId,
                        content: messageContent,
                        direction: 'outgoing',
                        timestamp: new Date(),
                        status: 'sent'
                    }]);
                    setMessageInput('');
                    setSelectedTemplate(null);
                    setTemplateParameters([]);
                    setShowTemplates(false);
                    // Refresh conversations to update last message
                    fetchConversations();
                    // Scroll to bottom after sending
                    setTimeout(() => scrollToBottom(), 100);
                }
            } else {
                const errorData = await response.json();
                alert(errorData.error || 'Error al enviar mensaje');
            }
        } catch (err) {
            console.error('Error sending message:', err);
            alert('Error al enviar mensaje');
        }
    };

    const selectConversation = (conversation) => {
        setSelectedConversation(conversation);
        fetchMessages(conversation.phoneNumber);
        
        // On mobile, show conversation view
        if (isMobile) {
            setShowMobileConversation(true);
            // Notify parent component
            if (onMobileChatOpenChange) {
                onMobileChatOpenChange(true);
            }
        }
        
        // Update local state to mark conversation as read
        setConversations(prevConversations => 
            prevConversations.map(conv => 
                conv.phoneNumber === conversation.phoneNumber 
                    ? { ...conv, unreadCount: 0 }
                    : conv
            )
        );
        
        // Scroll to bottom after messages load
        setTimeout(() => scrollToBottom(), 300);
    };
    
    const handleBackToConversations = () => {
        setShowMobileConversation(false);
        setSelectedConversation(null);
        // Notify parent component
        if (onMobileChatOpenChange) {
            onMobileChatOpenChange(false);
        }
    };
    
    // Copy conversation details to clipboard
    const copyConversationDetails = () => {
        if (!selectedConversation) return;
        
        const propertyLabels = {
            'departamento': 'Departamento',
            'casa': 'Casa',
            'ph': 'PH',
            'local': 'Local Comercial',
            'oficina': 'Oficina',
            'cocheras': 'Cochera',
            'lote': 'Lote/Terreno'
        };
        
        const operationLabels = {
            'venta': 'Venta',
            'alquiler': 'Alquiler'
        };
        
        const formatCurrency = (num) => {
            if (!num) return null;
            return `USD ${Number(num).toLocaleString('es-AR')}`;
        };
        
        // Build valuation string
        let valuationStr = null;
        if (selectedConversation.minPrice && selectedConversation.maxPrice) {
            valuationStr = `💰 Valuación: ${formatCurrency(selectedConversation.minPrice)} - ${formatCurrency(selectedConversation.maxPrice)}`;
        }
        
        // Build property details string
        let propertyDetails = [];
        if (selectedConversation.amb) propertyDetails.push(`${selectedConversation.amb} amb`);
        if (selectedConversation.sc) propertyDetails.push(`${selectedConversation.sc}m² cub`);
        if (selectedConversation.st) propertyDetails.push(`${selectedConversation.st}m² semi`);
        if (selectedConversation.garage) propertyDetails.push('con cochera');
        if (selectedConversation.antiguedad) propertyDetails.push(`${selectedConversation.antiguedad} años`);
        if (selectedConversation.condition) propertyDetails.push(`estado: ${selectedConversation.condition}`);
        
        const details = [
            `📱 Teléfono: +${selectedConversation.phoneNumber}`,
            selectedConversation.name ? `👤 Nombre: ${selectedConversation.name}` : null,
            selectedConversation.email ? `📧 Email: ${selectedConversation.email}` : null,
            '',
            selectedConversation.address ? `📍 Dirección: ${selectedConversation.address}` : null,
            selectedConversation.zone ? `🏘️ Zona: ${selectedConversation.zone}` : null,
            '',
            selectedConversation.property ? `🏠 Tipo: ${propertyLabels[selectedConversation.property] || selectedConversation.property}` : null,
            selectedConversation.operation ? `💼 Operación: ${operationLabels[selectedConversation.operation] || selectedConversation.operation}` : null,
            propertyDetails.length > 0 ? `📐 Detalles: ${propertyDetails.join(', ')}` : null,
            valuationStr,
            '',
            selectedConversation.motive ? `💡 Motivo: ${selectedConversation.motive}` : null,
        ].filter(line => line !== null).join('\n');
        
        navigator.clipboard.writeText(details).then(() => {
            // Show brief feedback
            const btn = document.getElementById('copy-details-btn');
            if (btn) {
                const originalText = btn.innerText;
                btn.innerText = '✅';
                setTimeout(() => { btn.innerText = originalText; }, 1500);
            }
        }).catch(err => {
            console.error('Error copying:', err);
            alert('Error al copiar');
        });
    };
    
    // Scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };
    
    // Filter conversations based on search
    const filteredConversations = conversations.filter(conv => {
        if (!searchQuery.trim()) return true;
        const query = searchQuery.toLowerCase();
        return (
            conv.name?.toLowerCase().includes(query) ||
            conv.phoneNumber?.toLowerCase().includes(query)
        );
    });

    const formatMessageTime = (timestamp) => {
        if (!timestamp) return '';

        const date = new Date(timestamp);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        
        if (diffMins < 1) return 'Ahora';
        if (diffMins < 60) return `${diffMins}m`;
        
        const diffHours = Math.floor(diffMins / 60);
        if (diffHours < 24) return `${diffHours}h`;
        
        const diffDays = Math.floor(diffHours / 24);
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `${diffDays}d`;
        
        return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' });
    };

    const exportSelectedTasaciones = () => {
        if (selectedTasaciones.length === 0) {
            alert('Por favor selecciona al menos una tasación para exportar');
            return;
        }

        let txtContent = '';
        selectedTasaciones.forEach((tasacion, index) => {
            // Address
            txtContent += tasacion.address || 'N/A';
            txtContent += '\n\n';

            // Venta/Alquiler
            txtContent += tasacion.operation === 'venta' ? 'venta' : tasacion.operation === 'alquiler' ? 'alquiler' : 'N/A';
            txtContent += '\n\n';

            // Price range
            txtContent += tasacion.priceRange || 'N/A';
            txtContent += '\n\n';

            // Phone
            txtContent += tasacion.phone || 'N/A';

            // Add 3 blank lines between tasaciones (except for the last one)
            if (index < selectedTasaciones.length - 1) {
                txtContent += '\n\n\n\n';
            }
        });

        // Create and download file
        const blob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `tasaciones_export_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Clear selection after export
        setSelectedTasaciones([]);
    };

    const openTasacionModal = (tasacion) => {
        setSelectedTasacionDetail(tasacion);
        setShowTasacionModal(true);
        // Pre-populate appointment name if available
        if (tasacion.email && tasacion.email !== 'N/A') {
            setAppointmentName(tasacion.email.split('@')[0]);
        } else {
            setAppointmentName('');
        }
    };

    const closeTasacionModal = () => {
        setShowTasacionModal(false);
        setSelectedTasacionDetail(null);
        // Clear appointment fields
        setAppointmentDate('');
        setAppointmentTime('');
        setAppointmentName('');
    };

    const saveAppointment = async () => {
        if (!appointmentDate || !appointmentTime || !appointmentName) {
            alert('Por favor completa todos los campos de la cita');
            return;
        }

        setSavingAppointment(true);
        try {
            const response = await fetch(`${apiBaseUrl}/api/admin/save-appointment`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${userToken}`
                },
                body: JSON.stringify({
                    tasacionId: selectedTasacionDetail._id,
                    date: appointmentDate,
                    time: appointmentTime,
                    clientName: appointmentName,
                    address: selectedTasacionDetail.address,
                    phone: selectedTasacionDetail.phone,
                    priceRange: selectedTasacionDetail.priceRange
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save appointment');
            }

            alert('✅ Cita guardada exitosamente');
            // Refresh appointments list if we're on the appointments tab
            if (activeTab === 'appointments') {
                await fetchAppointments();
            }
            closeTasacionModal();
        } catch (error) {
            console.error('Error saving appointment:', error);
            alert('Error al guardar la cita');
        } finally {
            setSavingAppointment(false);
        }
    };

    const copyTasacionToClipboard = (tasacion) => {
        const propertyLabels = {
            'departamento': 'Departamento',
            'casa': 'Casa',
            'ph': 'PH',
            'local': 'Local Comercial',
            'oficina': 'Oficina',
            'cocheras': 'Cochera'
        };
        
        const operationLabels = {
            'venta': 'Venta',
            'alquiler': 'Alquiler'
        };

        let details = `📋 DETALLES DE TASACIÓN\n`;
        details += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
        
        details += `📅 Fecha: ${tasacion.timestamp ? new Date(tasacion.timestamp).toLocaleDateString('es-AR', { 
            year: 'numeric',
            month: 'long', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }) : 'N/A'}\n\n`;
        
        details += `📍 DIRECCIÓN:\n${tasacion.address || 'N/A'}\n\n`;
        
        if (tasacion.zone) {
            details += `🏘️ Zona: ${tasacion.zone}\n\n`;
        }
        
        details += `🏠 PROPIEDAD:\n`;
        details += `Tipo: ${propertyLabels[tasacion.property] || tasacion.property || 'N/A'}\n`;
        details += `Operación: ${operationLabels[tasacion.operation] || tasacion.operation || 'N/A'}\n\n`;
        
        if (tasacion.amb || tasacion.sc || tasacion.st || tasacion.garage) {
            details += `📐 CARACTERÍSTICAS:\n`;
            if (tasacion.amb) details += `Ambientes: ${tasacion.amb}\n`;
            if (tasacion.sc) details += `Superficie cubierta: ${tasacion.sc}m²\n`;
            if (tasacion.st) details += `Superficie total: ${tasacion.st}m²\n`;
            if (tasacion.garage) details += `Cochera: ${tasacion.garage}\n`;
            if (tasacion.antiguedad) details += `Antigüedad: ${tasacion.antiguedad} años\n`;
            if (tasacion.condition) details += `Estado: ${tasacion.condition}\n`;
            details += `\n`;
        }
        
        if (tasacion.priceRange) {
            details += `💰 VALUACIÓN:\n${tasacion.priceRange}\n\n`;
        }
        
        details += `👤 CONTACTO:\n`;
        if (tasacion.phone) details += `📞 Teléfono: ${tasacion.phone}\n`;
        if (tasacion.email) details += `📧 Email: ${tasacion.email}\n`;
        if (tasacion.motive) details += `💡 Motivo: ${tasacion.motive}\n`;
        
        if (tasacion.ip) {
            details += `\n🌐 IP: ${tasacion.ip}`;
        }

        navigator.clipboard.writeText(details).then(() => {
            alert('✅ Datos copiados al portapapeles');
        }).catch(err => {
            console.error('Error copying to clipboard:', err);
            alert('❌ Error al copiar datos');
        });
    };

    // Fetch only tasaciones table (for pagination)
    const fetchTasaciones = async (page = 1) => {
        try {
            // Build query parameters for date range and search
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            if (addressSearch && addressSearch.trim()) params.append('search', addressSearch.trim());
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const tasacionesResponse = await fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers });
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching tasaciones:', err);
        }
    };

    const fetchMetrics = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters for date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const [platformResponse, tasacionesResponse, leadSourcesResponse, funnelResponse, conversationsResponse] = await Promise.all([
                fetch(`${apiBaseUrl}/api/metrics/platform${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/lead-sources${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/form-funnel${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/whatsapp/conversations-count${queryString}`, { headers })
            ]);

            // Check for authorization errors
            if (platformResponse.status === 403 || platformResponse.status === 401) {
                setError('Acceso denegado. Se requieren privilegios de administrador.');
                setLoading(false);
                return;
            }

            if (platformResponse.ok) setPlatformMetrics(await platformResponse.json());
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
            
            if (leadSourcesResponse.ok) {
                const data = await leadSourcesResponse.json();
                setLeadSources(data);
            }
            
            if (funnelResponse.ok) {
                const data = await funnelResponse.json();
                setFunnelMetrics(data);
            }
            
            if (conversationsResponse.ok) {
                const data = await conversationsResponse.json();
                setConversationsCount(data.count || 0);
            }
            
            await fetchRealtimeFallback();
        } catch (err) {
            setError('Error al cargar las métricas');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => num?.toLocaleString?.() ?? num;
    const formatCurrency = (num) => `$${Number(num || 0).toLocaleString()}`;
    
    // Generate PDF with metrics
    const generatePDF = async () => {
        // First, refresh the metrics
        setLoading(true);
        await fetchMetrics(currentPage);
        setLoading(false);
        
        // Generate PDF immediately after data is loaded
        const doc = new jsPDF();
        let yPosition = 20;

        // Add logo (maintaining aspect ratio - approximately square/circular logo)
        try {
            doc.addImage(logoMain, 'PNG', 15, yPosition, 30, 30);
            yPosition += 35;
        } catch (err) {
            console.error('Error adding logo to PDF:', err);
            yPosition += 10;
        }

        // Title
        doc.setFontSize(20);
        doc.setFont('helvetica', 'bold');
        doc.text('Reporte de Metricas', 105, yPosition, { align: 'center' });
        yPosition += 10;

        // Date range
        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        const dateRangeText = startDate || endDate 
            ? `Periodo: ${startDate || 'inicio'} a ${endDate || 'ahora'}`
            : 'Periodo: Todos los datos';
        doc.text(dateRangeText, 105, yPosition, { align: 'center' });
        yPosition += 15;

        // Platform Metrics Section
        if (platformMetrics) {
            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Metricas de Plataforma', 15, yPosition);
            yPosition += 8;

            // Calculate conversion rate from total page visitors to step 1 completion
            const totalPageVisitors = leadSources?.totals?.totalVisitors || 0;
            const step1Completions = funnelMetrics?.funnel?.[0]?.uniqueUsers || 0;
            const visitorToStep1Rate = totalPageVisitors > 0 
                ? Math.round((step1Completions / totalPageVisitors) * 100) 
                : 0;
            
            doc.autoTable({
                startY: yPosition,
                head: [['Metrica', 'Valor']],
                body: [
                    ['Total Valuaciones', formatNumber(platformMetrics.totalValuations || 0)],
                    ['Visitantes de la Pagina', formatNumber(totalPageVisitors)],
                    ['Iniciaron Formulario', `${formatNumber(step1Completions)} (${visitorToStep1Rate}%)`],
                    ['Conversaciones Iniciadas', formatNumber(conversationsCount)],
                    ['Total Usuarios Registrados', formatNumber(platformMetrics.totalUsers || 0)],
                    ['Valuaciones CABA', formatNumber(platformMetrics.cabaValuations || 0)],
                    ['Valuaciones Provincia', formatNumber(platformMetrics.provinciaValuations || 0)],
                    ['Rango Promedio de Precio', platformMetrics.avgPriceRange || 'N/A'],
                    ['Mensajes WhatsApp Pendientes', formatNumber(platformMetrics.scheduledWhatsAppMessages || 0)]
                ],
                theme: 'striped',
                headStyles: { fillColor: [102, 126, 234] },
                margin: { left: 15, right: 15 }
            });
            yPosition = doc.lastAutoTable.finalY + 12;
        }

        // Lead Sources Section
        if (leadSources && leadSources.leadSources && leadSources.leadSources.length > 0) {
            if (yPosition > 250) {
                doc.addPage();
                yPosition = 20;
            }

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Fuentes de Trafico', 15, yPosition);
            yPosition += 8;

            // Map source names to friendly display names (without emojis for PDF)
            const getSourceName = (sourceName) => {
                const sourceMap = {
                    'google_ads': 'Google Ads',
                    'tiktok_ads': 'TikTok Ads',
                    'facebook_ads': 'Facebook Ads',
                    'instagram_ads': 'Instagram Ads',
                    'google_organic': 'Google Organico',
                    'facebook_organic': 'Facebook Organico',
                    'instagram_organic': 'Instagram Organico',
                    'tiktok_organic': 'TikTok Organico',
                    'linkedin_organic': 'LinkedIn Organico',
                    'twitter_organic': 'Twitter Organico',
                    'youtube': 'YouTube',
                    'email': 'Email',
                    'direct': 'Directo',
                    'referral': 'Referido',
                    'organic': 'Organico',
                    'paid': 'Pago',
                    'social': 'Redes Sociales',
                    'unknown': 'Desconocido'
                };
                return sourceMap[sourceName] || sourceName || 'Directo';
            };
            
            const leadSourcesData = leadSources.leadSources.map(source => [
                getSourceName(source.source),
                formatNumber(source.visitors),
                formatNumber(source.conversions),
                `${source.conversionRate}%`
            ]);

            doc.autoTable({
                startY: yPosition,
                head: [['Fuente', 'Visitantes', 'Conversiones', 'Tasa Conv']],
                body: leadSourcesData,
                theme: 'striped',
                headStyles: { fillColor: [52, 152, 219] },
                margin: { left: 15, right: 15 }
            });
            yPosition = doc.lastAutoTable.finalY + 12;
        }

        // Funnel Metrics Section
        if (funnelMetrics && funnelMetrics.summary && funnelMetrics.summary.totalStarts > 0) {
            if (yPosition > 220) {
                doc.addPage();
                yPosition = 20;
            }

            doc.setFontSize(14);
            doc.setFont('helvetica', 'bold');
            doc.text('Embudo de Conversion', 15, yPosition);
            yPosition += 8;

            // Summary
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Total Iniciaron: ${formatNumber(funnelMetrics.summary.totalStarts)}`, 15, yPosition);
            yPosition += 5;
            doc.text(`Total Completaron: ${formatNumber(funnelMetrics.summary.totalCompletions)}`, 15, yPosition);
            yPosition += 5;
            doc.text(`Tasa de Conversion: ${funnelMetrics.summary.overallConversionRate}%`, 15, yPosition);
            yPosition += 8;

            const funnelData = funnelMetrics.funnel.map(step => [
                `Paso ${step.step}: ${step.name}`,
                formatNumber(step.uniqueUsers),
                `${step.completionRate}%`,
                step.dropoffRate > 0 ? `${step.dropoffRate}%` : '-'
            ]);

            doc.autoTable({
                startY: yPosition,
                head: [['Paso', 'Usuarios', 'Completaron', 'Abandonaron']],
                body: funnelData,
                theme: 'striped',
                headStyles: { fillColor: [39, 174, 96] },
                margin: { left: 15, right: 15 }
            });
            yPosition = doc.lastAutoTable.finalY + 5;

            if (funnelMetrics.summary.biggestDropoff && funnelMetrics.summary.biggestDropoff.rate > 0) {
                yPosition += 5;
                doc.setFontSize(9);
                doc.setTextColor(133, 100, 4);
                doc.text(
                    `ALERTA: Mayor abandono: ${funnelMetrics.summary.biggestDropoff.rate}% en ${funnelMetrics.summary.biggestDropoff.name}`,
                    15,
                    yPosition
                );
            }
        }

        // Footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(150);
            doc.text(
                `TasacionesYa - Generado el ${new Date().toLocaleDateString('es-AR')} - Pagina ${i} de ${pageCount}`,
                105,
                290,
                { align: 'center' }
            );
        }

        // Save PDF
        const fileName = `metricas_${startDate || 'inicio'}_${endDate || 'fin'}.pdf`;
        doc.save(fileName);
    };

    // Generate JSON with metrics data
    const generateJSON = async () => {
        // First, refresh the metrics
        setLoading(true);
        await fetchMetrics(currentPage);
        setLoading(false);
        
        // Build comprehensive JSON data
        const jsonData = {
            metadata: {
                reportType: 'Reporte de Metricas TasacionesYa',
                generatedAt: new Date().toISOString(),
                dateRange: {
                    startDate: startDate || 'inicio',
                    endDate: endDate || 'ahora'
                }
            },
            platformMetrics: {
                totalValuations: platformMetrics?.totalValuations || 0,
                totalUsers: platformMetrics?.totalUsers || 0,
                cabaValuations: platformMetrics?.cabaValuations || 0,
                provinciaValuations: platformMetrics?.provinciaValuations || 0,
                avgPriceRange: platformMetrics?.avgPriceRange || 'N/A',
                scheduledWhatsAppMessages: platformMetrics?.scheduledWhatsAppMessages || 0
            },
            trafficMetrics: {
                totalVisitors: leadSources?.totals?.totalVisitors || 0,
                totalConversions: leadSources?.totals?.totalConversions || 0,
                totalUniqueUserConversions: leadSources?.totals?.totalUniqueUserConversions || 0,
                avgConversionRate: `${leadSources?.totals?.avgConversionRate || 0}%`,
                sources: leadSources?.leadSources?.map(source => ({
                    name: source.source || 'unknown',
                    visitors: source.visitors || 0,
                    conversions: source.conversions || 0,
                    uniqueUserConversions: source.uniqueUserConversions || 0,
                    conversionRate: `${source.conversionRate || 0}%`,
                    avgPageViews: source.avgPageViews || 0
                })) || []
            },
            conversionFunnel: {
                summary: {
                    totalStarts: funnelMetrics?.summary?.totalStarts || 0,
                    totalCompletions: funnelMetrics?.summary?.totalCompletions || 0,
                    overallConversionRate: `${funnelMetrics?.summary?.overallConversionRate || 0}%`,
                    biggestDropoff: funnelMetrics?.summary?.biggestDropoff ? {
                        step: funnelMetrics.summary.biggestDropoff.step,
                        name: funnelMetrics.summary.biggestDropoff.name,
                        rate: `${funnelMetrics.summary.biggestDropoff.rate}%`
                    } : null
                },
                steps: funnelMetrics?.funnel?.map(step => ({
                    step: step.step,
                    name: step.name,
                    uniqueUsers: step.uniqueUsers,
                    completionRate: `${step.completionRate}%`,
                    dropoffRate: `${step.dropoffRate}%`
                })) || []
            },
            whatsappMetrics: {
                conversationsStarted: conversationsCount || 0
            }
        };
        
        // Convert to JSON string with formatting
        const jsonString = JSON.stringify(jsonData, null, 2);
        
        // Create blob and download
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `metricas_${startDate || 'inicio'}_${endDate || 'fin'}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    
    const fetchSettings = async () => {
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setWhatsappVerificationEnabled(data.settings.whatsappVerificationEnabled);
                    setAutoSendWhatsAppTemplate(data.settings.autoSendWhatsAppTemplate || false);
                    setRequirePhoneNumber(data.settings.requirePhoneNumber ?? true);
                    setSendTasacionCompletionEmails(data.settings.sendTasacionCompletionEmails ?? true);
                    setEnablePayOption(data.settings.enablePayOption ?? false);
                    setPayOptionPrice(data.settings.payOptionPrice ?? 1000);
                    setPayOptionDiscountPercent(data.settings.payOptionDiscountPercent ?? 0);
                    setEnableFreeTasacion(data.settings.enableFreeTasacion ?? true);
                }
            }
        } catch (err) {
            console.error('Error fetching settings:', err);
        } finally {
            setSettingsLoading(false);
        }
    };
    
    const toggleWhatsappVerification = async () => {
        const newValue = !whatsappVerificationEnabled;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ whatsappVerificationEnabled: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setWhatsappVerificationEnabled(newValue);
                    alert(`✅ WhatsApp verificación ${newValue ? 'activada' : 'desactivada'} correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };
    
    const toggleAutoSendTemplate = async () => {
        const newValue = !autoSendWhatsAppTemplate;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ autoSendWhatsAppTemplate: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setAutoSendWhatsAppTemplate(newValue);
                    alert(`✅ Envío automático de plantillas ${newValue ? 'activado' : 'desactivado'} correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };
    
    const toggleRequirePhoneNumber = async () => {
        const newValue = !requirePhoneNumber;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ requirePhoneNumber: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setRequirePhoneNumber(newValue);
                    alert(`✅ Teléfono ${newValue ? 'requerido' : 'opcional'} actualizado correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };
    
    const toggleSendTasacionCompletionEmails = async () => {
        const newValue = !sendTasacionCompletionEmails;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ sendTasacionCompletionEmails: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setSendTasacionCompletionEmails(newValue);
                    alert(`✅ Emails de tasación ${newValue ? 'activados' : 'desactivados'} correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };

    const toggleEnablePayOption = async () => {
        const newValue = !enablePayOption;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ enablePayOption: newValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setEnablePayOption(newValue);
                    alert(`✅ Opción de pago ${newValue ? 'activada' : 'desactivada'} correctamente`);
                }
            } else {
                alert('❌ Error al actualizar configuración');
            }
        } catch (err) {
            console.error('Error updating settings:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };

    const updatePayOptionPrice = async () => {
        const priceValue = parseFloat(payOptionPrice);
        
        if (isNaN(priceValue) || priceValue < 0) {
            alert('❌ Por favor ingresá un precio válido (mayor o igual a 0)');
            return;
        }
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ payOptionPrice: priceValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    alert(`✅ Precio actualizado a $${priceValue.toLocaleString('es-AR')}`);
                }
            } else {
                alert('❌ Error al actualizar precio');
            }
        } catch (err) {
            console.error('Error updating price:', err);
            alert('❌ Error al actualizar precio');
        } finally {
            setSettingsLoading(false);
        }
    };

    const updatePayOptionDiscount = async () => {
        const discountValue = parseFloat(payOptionDiscountPercent);
        
        if (isNaN(discountValue) || discountValue < 0 || discountValue > 100) {
            alert('❌ Por favor ingresá un descuento válido (entre 0 y 100)');
            return;
        }
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ payOptionDiscountPercent: discountValue })
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    alert(`✅ Descuento actualizado a ${discountValue}%`);
                }
            } else {
                alert('❌ Error al actualizar descuento');
            }
        } catch (err) {
            console.error('Error updating discount:', err);
            alert('❌ Error al actualizar descuento');
        } finally {
            setSettingsLoading(false);
        }
    };

    const toggleEnableFreeTasacion = async () => {
        const newValue = !enableFreeTasacion;
        
        try {
            setSettingsLoading(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const payload = { enableFreeTasacion: newValue };
            console.log('🔧 Sending enableFreeTasacion update:', payload, 'Type:', typeof newValue);
            
            const res = await fetch(`${apiBaseUrl}/api/admin/settings`, {
                method: 'POST',
                headers,
                body: JSON.stringify(payload)
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setEnableFreeTasacion(newValue);
                    console.log(`✅ Free Tasacion ${newValue ? 'enabled' : 'disabled'}`);
                } else {
                    console.error('Error response:', data);
                    alert(`❌ Error: ${data.error || 'Error al actualizar configuración'}`);
                }
            } else {
                const errorData = await res.json().catch(() => ({}));
                console.error('Error response:', errorData);
                alert(`❌ Error: ${errorData.error || 'Error al actualizar configuración'}`);
            }
        } catch (err) {
            console.error('Error updating free tasacion setting:', err);
            alert('❌ Error al actualizar configuración');
        } finally {
            setSettingsLoading(false);
        }
    };

    // Send pending WhatsApp messages manually
    const sendPendingWhatsAppMessages = async () => {
        if (!window.confirm('¿Estás seguro de que querés enviar todos los mensajes de WhatsApp pendientes ahora?')) {
            return;
        }
        
        try {
            setSendingPendingWhatsApp(true);
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/send-pending-whatsapp`, {
                method: 'POST',
                headers
            });
            
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    alert(`✅ ${data.message}\n\nEnviados: ${data.sent}\nFallidos: ${data.failed}\nTotal: ${data.total}`);
                } else {
                    alert(`❌ ${data.error || 'Error al enviar mensajes'}`);
                }
            } else {
                alert('❌ Error al enviar mensajes de WhatsApp');
            }
        } catch (err) {
            console.error('Error sending pending WhatsApp:', err);
            alert('❌ Error al enviar mensajes de WhatsApp');
        } finally {
            setSendingPendingWhatsApp(false);
        }
    };

    // Helper functions - defined before they are used
    const getLoadingText = () => {
        switch(loadingSection) {
            case 'platform': return 'Cargando métricas...';
            case 'tasaciones': return 'Cargando tasaciones...';
            case 'leadSources': return 'Cargando leads...';
            case 'appointments': return 'Cargando citas...';
            case 'messages': return 'Cargando mensajes...';
            case 'settings': return 'Cargando ajustes...';
            default: return 'Cargando...';
        }
    };

    const getPageTitle = () => {
        switch(activeTab) {
            case 'platform': return '📊 Métricas de Plataforma';
            case 'tasaciones': return '🏠 Tasaciones';
            case 'leadSources': return '🎯 Fuentes de Leads';
            case 'appointments': return '📅 Citas Martillero';
            case 'messages': return '💬 Mensajes WhatsApp';
            case 'settings': return '⚙️ Ajustes';
            default: return '📊 Dashboard';
        }
    };

    // Show breathing loader while data is being fetched
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                fontFamily: 'Arial, sans-serif'
            }}>
                <style>
                    {`
                        @keyframes breathe {
                            0%, 100% {
                                transform: scale(1);
                                opacity: 0.8;
                            }
                            50% {
                                transform: scale(1.1);
                                opacity: 1;
                            }
                        }
                    `}
                </style>
                <img 
                    src={logoMain} 
                    alt="Loading" 
                    style={{
                        width: '150px',
                        height: 'auto',
                        marginBottom: '30px',
                        animation: 'breathe 2s ease-in-out infinite'
                    }}
                />
                <p style={{
                    fontSize: '18px',
                    color: '#667eea',
                    fontWeight: '500',
                    margin: 0
                }}>
                    {getLoadingText()}
                </p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#e74c3c' }}>
                ❌ {error}
            </div>
        );
    }

    // Loading Component disabled - now handled at App.js level
    // if (sectionLoading) {
    //     return (
    //         <div style={{
    //             display: 'flex',
    //             flexDirection: 'column',
    //             alignItems: 'center',
    //             justifyContent: 'center',
    //             minHeight: '60vh',
    //             fontFamily: 'Arial, sans-serif'
    //         }}>
    //             <style>
    //                 {`
    //                     @keyframes breathe {
    //                         0%, 100% {
    //                             transform: scale(1);
    //                             opacity: 0.8;
    //                         }
    //                         50% {
    //                             transform: scale(1.1);
    //                             opacity: 1;
    //                         }
    //                     }
    //                 `}
    //             </style>
    //             <img 
    //                 src={logoMain} 
    //                 alt="Loading" 
    //                 style={{
    //                     width: '150px',
    //                     height: 'auto',
    //                     marginBottom: '30px',
    //                     animation: 'breathe 2s ease-in-out infinite'
    //                 }}
    //             />
    //             <p style={{
    //                 fontSize: '18px',
    //                 color: '#667eea',
    //                 fontWeight: '500',
    //                 margin: 0
    //             }}>
    //                 {getLoadingText()}
    //             </p>
    //         </div>
    //     );
    // }

    return (
        <div style={{ 
            maxWidth: activeTab === 'messages' ? 'none' : '1200px', 
            margin: activeTab === 'messages' ? '0' : '0 auto', 
            padding: activeTab === 'messages' ? '0' : (isMobile ? '15px' : '20px'), 
            fontFamily: 'Arial, sans-serif' 
        }}>
            {activeTab !== 'messages' && (
                <h1 style={{ 
                    textAlign: 'center', 
                    color: '#2c3e50', 
                    marginBottom: isMobile ? '15px' : '20px', 
                    fontSize: isMobile ? '22px' : '28px',
                    fontWeight: '700'
                }}>
                    {getPageTitle()}
                </h1>
            )}

            {/* Real-time notification */}
            {realtimeNotification && (
                <div style={{ 
                    backgroundColor: '#27ae60', 
                    color: 'white', 
                    padding: '12px 20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    animation: 'fadeIn 0.3s ease-in'
                }}>
                    🔔 {realtimeNotification.message}
                </div>
            )}

            {/* Date Range Filter - Only show for platform, tasaciones, and leadSources */}
            {(activeTab === 'platform' || activeTab === 'tasaciones' || activeTab === 'leadSources') && (
            <div style={{ 
                backgroundColor: 'white', 
                padding: isMobile ? '15px' : '20px', 
                borderRadius: isMobile ? '10px' : '12px', 
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                marginBottom: isMobile ? '15px' : '20px'
            }}>
                {/* Status Text */}
                <div style={{ 
                    width: '100%', 
                    textAlign: 'center', 
                    marginBottom: '15px', 
                    color: '#667eea', 
                    fontSize: isMobile ? '12px' : '13px',
                    fontWeight: '500'
                }}>
                    {!startDate && !endDate ? '📊 Mostrando todos los datos' : `📅 ${startDate || 'inicio'} → ${endDate || 'ahora'}`}
                </div>
                
                {/* Date Inputs - Mobile: Stacked, Desktop: Row */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '12px' : '15px',
                    marginBottom: '15px'
                }}>
                    <div style={{ flex: 1 }}>
                        <label style={{ 
                            display: 'block',
                            fontWeight: '600', 
                            color: '#2c3e50',
                            fontSize: isMobile ? '13px' : '14px',
                            marginBottom: '6px'
                        }}>
                            Desde:
                        </label>
                        <input 
                            type="date" 
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            style={{ 
                                width: '100%',
                                padding: isMobile ? '10px' : '8px 12px', 
                                border: '2px solid #e1e8ed', 
                                borderRadius: '8px',
                                fontSize: isMobile ? '15px' : '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{ 
                            display: 'block',
                            fontWeight: '600', 
                            color: '#2c3e50',
                            fontSize: isMobile ? '13px' : '14px',
                            marginBottom: '6px'
                        }}>
                            Hasta:
                        </label>
                        <input 
                            type="date" 
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            style={{ 
                                width: '100%',
                                padding: isMobile ? '10px' : '8px 12px', 
                                border: '2px solid #e1e8ed', 
                                borderRadius: '8px',
                                fontSize: isMobile ? '15px' : '14px',
                                outline: 'none',
                                transition: 'border-color 0.2s'
                            }}
                        />
                    </div>
                </div>
                
                {/* Action Buttons - Mobile: Full width stacked, Desktop: Row */}
                <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
                    gap: isMobile ? '10px' : '12px'
                }}>
                    <button 
                        onClick={() => fetchMetrics(1)}
                        style={{ 
                            padding: isMobile ? '12px' : '10px 20px', 
                            backgroundColor: '#667eea', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: isMobile ? '15px' : '14px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(102, 126, 234, 0.3)'
                        }}
                    >
                        🔍 Filtrar
                    </button>
                    <button 
                        onClick={() => {
                            const today = getTodayDate();
                            setStartDate(today);
                            setEndDate(today);
                            setTimeout(fetchMetrics, 100);
                        }}
                        style={{ 
                            padding: isMobile ? '12px' : '10px 20px', 
                            backgroundColor: '#27ae60', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: isMobile ? '15px' : '14px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(39, 174, 96, 0.3)'
                        }}
                    >
                        📅 Hoy
                    </button>
                    <button 
                        onClick={() => {
                            setStartDate('');
                            setEndDate('');
                            setTimeout(fetchMetrics, 100);
                        }}
                        style={{ 
                            padding: isMobile ? '12px' : '10px 20px', 
                            backgroundColor: '#95a5a6', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: isMobile ? '15px' : '14px',
                            transition: 'all 0.2s',
                            boxShadow: '0 2px 4px rgba(149, 165, 166, 0.3)'
                        }}
                    >
                        ∞ Todo
                    </button>
                    <button 
                        onClick={generatePDF}
                        disabled={!platformMetrics || loading}
                        style={{ 
                            padding: isMobile ? '12px' : '10px 20px', 
                            backgroundColor: (platformMetrics && !loading) ? '#e74c3c' : '#bdc3c7', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: (platformMetrics && !loading) ? 'pointer' : 'not-allowed',
                            fontWeight: '600',
                            fontSize: isMobile ? '15px' : '14px',
                            transition: 'all 0.2s',
                            boxShadow: (platformMetrics && !loading) ? '0 2px 4px rgba(231, 76, 60, 0.3)' : 'none',
                            opacity: (platformMetrics && !loading) ? 1 : 0.6
                        }}
                    >
                        {loading ? '⏳' : '📄'} PDF
                    </button>
                    <button 
                        onClick={generateJSON}
                        disabled={!platformMetrics || loading}
                        style={{ 
                            padding: isMobile ? '12px' : '10px 20px', 
                            backgroundColor: (platformMetrics && !loading) ? '#9b59b6' : '#bdc3c7', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px', 
                            cursor: (platformMetrics && !loading) ? 'pointer' : 'not-allowed',
                            fontWeight: '600',
                            fontSize: isMobile ? '15px' : '14px',
                            transition: 'all 0.2s',
                            boxShadow: (platformMetrics && !loading) ? '0 2px 4px rgba(155, 89, 182, 0.3)' : 'none',
                            opacity: (platformMetrics && !loading) ? 1 : 0.6
                        }}
                    >
                        {loading ? '⏳' : '📊'} JSON
                    </button>
                </div>
            </div>
            )}

            {/* Live tiles row - Show only on platform metrics */}
            {activeTab === 'platform' && (
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', 
                gap: isMobile ? '12px' : '16px', 
                marginBottom: isMobile ? '15px' : '20px' 
            }}>
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: isMobile ? '18px' : '20px', 
                    borderRadius: isMobile ? '10px' : '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                    border: '1px solid #e8ecef'
                }}>
                    <div style={{ 
                        fontSize: isMobile ? '13px' : '14px', 
                        color: '#7f8c8d', 
                        marginBottom: '8px',
                        fontWeight: '500'
                    }}>
                        Usuarios activos ahora
                    </div>
                    <div style={{ 
                        fontSize: isMobile ? '28px' : '34px', 
                        fontWeight: '800', 
                        color: '#8e44ad',
                        lineHeight: 1
                    }}>
                        {activeUsers ?? '—'}
                    </div>
                    <div style={{ 
                        fontSize: isMobile ? '11px' : '12px', 
                        color: '#95a5a6', 
                        marginTop: '6px' 
                    }}>
                        Tiempo real (Socket.IO)
                    </div>
                </div>
            </div>
            )}

            {/* Tab Navigation - Hidden when using sidebar navigation */}

            {/* Platform Metrics Tab */}
            {activeTab === 'platform' && platformMetrics && (
                <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))', 
                    gap: isMobile ? '12px' : '16px' 
                }}>
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #e8ecef' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            🏠 Tasaciones Totales
                        </h3>
                        <div style={{ 
                            fontSize: isMobile ? '26px' : '30px', 
                            fontWeight: '700', 
                            color: '#3498db',
                            lineHeight: 1
                        }}>
                            {formatNumber(platformMetrics.totalValuations)}
                        </div>
                        <div style={{ 
                            color: '#7f8c8d', 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '8px' 
                        }}>
                            Últimos 30 días: {formatNumber(platformMetrics.recentValuations)}
                        </div>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #e8ecef' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            👤 Usuarios Únicos
                        </h3>
                        <div style={{ 
                            fontSize: isMobile ? '26px' : '30px', 
                            fontWeight: '700', 
                            color: '#9b59b6',
                            lineHeight: 1
                        }}>
                            {formatNumber(platformMetrics.uniqueUsersCount)}
                        </div>
                        <div style={{ 
                            color: '#7f8c8d', 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '8px' 
                        }}>
                            Teléfonos únicos con tasaciones
                        </div>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #e8ecef' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            👥 Usuarios Registrados
                        </h3>
                        <div style={{ 
                            fontSize: isMobile ? '26px' : '30px', 
                            fontWeight: '700', 
                            color: '#27ae60',
                            lineHeight: 1
                        }}>
                            {formatNumber(platformMetrics.totalUsers)}
                        </div>
                        <div style={{ 
                            color: '#7f8c8d', 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '8px' 
                        }}>
                            Nuevos (30 días): {formatNumber(platformMetrics.newUsers)}
                        </div>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #e8ecef' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            🏙️ Distribución por Zona
                        </h3>
                        <div style={{ 
                            marginBottom: '8px',
                            fontSize: isMobile ? '14px' : '15px'
                        }}>
                            <span style={{ color: '#3498db', fontWeight: '600' }}>CABA:</span> 
                            <span style={{ marginLeft: '8px', fontWeight: '500' }}>{formatNumber(platformMetrics.cabaValuations)}</span>
                        </div>
                        <div style={{
                            fontSize: isMobile ? '14px' : '15px'
                        }}>
                            <span style={{ color: '#e67e22', fontWeight: '600' }}>Provincia:</span> 
                            <span style={{ marginLeft: '8px', fontWeight: '500' }}>{formatNumber(platformMetrics.provinciaValuations)}</span>
                        </div>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #e8ecef' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            💰 Precio Promedio
                        </h3>
                        <div style={{ 
                            fontSize: isMobile ? '20px' : '24px', 
                            fontWeight: '700', 
                            color: '#f39c12',
                            lineHeight: 1
                        }}>
                            {platformMetrics.avgPriceRange}
                        </div>
                        <div style={{ 
                            color: '#7f8c8d', 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '8px' 
                        }}>
                            Rango promedio por tasación
                        </div>
                    </div>
                    
                    <div style={{ 
                        backgroundColor: 'white', 
                        padding: isMobile ? '18px' : '22px', 
                        borderRadius: isMobile ? '10px' : '12px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)', 
                        border: '1px solid #25d366' 
                    }}>
                        <h3 style={{ 
                            color: '#2c3e50', 
                            marginBottom: '12px', 
                            fontSize: isMobile ? '15px' : '17px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}>
                            📱 WhatsApp Pendientes
                        </h3>
                        <div style={{ 
                            fontSize: isMobile ? '26px' : '30px', 
                            fontWeight: '700', 
                            color: '#25d366',
                            lineHeight: 1
                        }}>
                            {formatNumber(platformMetrics.scheduledWhatsAppMessages || 0)}
                        </div>
                        <div style={{ 
                            color: '#7f8c8d', 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '8px' 
                        }}>
                            Se enviarán a las 8:30 AM
                        </div>
                    </div>
                </div>
            )}

            {/* User Experience / Conversion Funnel Section */}
            {activeTab === 'platform' && funnelMetrics && funnelMetrics.summary?.totalStarts > 0 && (
                <div style={{
                    marginTop: isMobile ? '20px' : '30px',
                    backgroundColor: 'white',
                    padding: isMobile ? '20px' : '25px',
                    borderRadius: isMobile ? '10px' : '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e1e8ed'
                }}>
                    <h2 style={{
                        color: '#2c3e50',
                        marginBottom: isMobile ? '15px' : '20px',
                        fontSize: isMobile ? '18px' : '22px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}>
                        📊 Experiencia del Usuario - Tasa de Conversión
                    </h2>

                    {/* Summary Cards */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
                        gap: isMobile ? '12px' : '16px',
                        marginBottom: isMobile ? '20px' : '25px'
                    }}>
                        {/* Total Starts */}
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            padding: isMobile ? '16px' : '20px',
                            borderRadius: '10px',
                            border: '2px solid #3498db'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '12px' : '13px',
                                color: '#7f8c8d',
                                marginBottom: '8px',
                                fontWeight: '600'
                            }}>
                                👥 USUARIOS QUE INICIARON
                            </div>
                            <div style={{
                                fontSize: isMobile ? '28px' : '32px',
                                fontWeight: '700',
                                color: '#3498db'
                            }}>
                                {formatNumber(funnelMetrics.summary?.totalStarts || 0)}
                            </div>
                            <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: '#95a5a6',
                                marginTop: '4px'
                            }}>
                                Seleccionaron dirección
                            </div>
                        </div>

                        {/* Total Completions */}
                        <div style={{
                            backgroundColor: '#f8f9fa',
                            padding: isMobile ? '16px' : '20px',
                            borderRadius: '10px',
                            border: '2px solid #27ae60'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '12px' : '13px',
                                color: '#7f8c8d',
                                marginBottom: '8px',
                                fontWeight: '600'
                            }}>
                                ✅ USUARIOS QUE COMPLETARON
                            </div>
                            <div style={{
                                fontSize: isMobile ? '28px' : '32px',
                                fontWeight: '700',
                                color: '#27ae60'
                            }}>
                                {formatNumber(funnelMetrics.summary?.totalCompletions || 0)}
                            </div>
                            <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: '#95a5a6',
                                marginTop: '4px'
                            }}>
                                Obtuvieron valuación
                            </div>
                        </div>

                        {/* Conversion Rate */}
                        <div style={{
                            backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            padding: isMobile ? '16px' : '20px',
                            borderRadius: '10px',
                            border: '2px solid #667eea'
                        }}>
                            <div style={{
                                fontSize: isMobile ? '12px' : '13px',
                                color: '#ffffff',
                                marginBottom: '8px',
                                fontWeight: '600',
                                opacity: 0.9
                            }}>
                                🎯 TASA DE CONVERSIÓN
                            </div>
                            <div style={{
                                fontSize: isMobile ? '28px' : '32px',
                                fontWeight: '700',
                                color: '#ffffff'
                            }}>
                                {funnelMetrics.summary?.overallConversionRate || 0}%
                            </div>
                            <div style={{
                                fontSize: isMobile ? '11px' : '12px',
                                color: '#ffffff',
                                marginTop: '4px',
                                opacity: 0.8
                            }}>
                                De inicio a completado
                            </div>
                        </div>
                    </div>

                    {/* Funnel Steps */}
                    <div style={{
                        backgroundColor: '#f8f9fa',
                        padding: isMobile ? '15px' : '20px',
                        borderRadius: '10px',
                        marginTop: isMobile ? '15px' : '20px'
                    }}>
                        <h3 style={{
                            fontSize: isMobile ? '15px' : '17px',
                            color: '#2c3e50',
                            marginBottom: isMobile ? '12px' : '15px',
                            fontWeight: '600'
                        }}>
                            📉 Desglose por Paso
                        </h3>

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: isMobile ? '10px' : '12px'
                        }}>
                            {funnelMetrics.funnel?.map((step, index) => (
                                <div key={index} style={{
                                    backgroundColor: 'white',
                                    padding: isMobile ? '12px' : '15px',
                                    borderRadius: '8px',
                                    border: '1px solid #e1e8ed',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                                        gap: '10px'
                                    }}>
                                        {/* Step Name */}
                                        <div style={{
                                            flex: isMobile ? '1 1 100%' : '1',
                                            fontSize: isMobile ? '13px' : '14px',
                                            fontWeight: '600',
                                            color: '#2c3e50'
                                        }}>
                                            Paso {step.step}: {step.name}
                                        </div>

                                        {/* Numbers and Percentages */}
                                        <div style={{
                                            display: 'flex',
                                            gap: isMobile ? '15px' : '20px',
                                            alignItems: 'center',
                                            fontSize: isMobile ? '12px' : '13px'
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: isMobile ? 'flex-start' : 'flex-end'
                                            }}>
                                                <span style={{
                                                    fontWeight: '700',
                                                    color: '#3498db',
                                                    fontSize: isMobile ? '15px' : '16px'
                                                }}>
                                                    {formatNumber(step.count)}
                                                </span>
                                                <span style={{
                                                    fontSize: isMobile ? '10px' : '11px',
                                                    color: '#95a5a6'
                                                }}>
                                                    usuarios
                                                </span>
                                            </div>

                                            <div style={{
                                                backgroundColor: step.completionRate >= 50 ? '#d4edda' : step.completionRate >= 25 ? '#fff3cd' : '#f8d7da',
                                                color: step.completionRate >= 50 ? '#155724' : step.completionRate >= 25 ? '#856404' : '#721c24',
                                                padding: isMobile ? '4px 10px' : '6px 12px',
                                                borderRadius: '6px',
                                                fontWeight: '700',
                                                fontSize: isMobile ? '13px' : '14px',
                                                minWidth: isMobile ? '50px' : '60px',
                                                textAlign: 'center'
                                            }}>
                                                {step.completionRate}%
                                            </div>

                                            {step.dropoffRate > 0 && (
                                                <div style={{
                                                    color: '#e74c3c',
                                                    fontSize: isMobile ? '11px' : '12px',
                                                    fontWeight: '600',
                                                    minWidth: isMobile ? '60px' : '80px',
                                                    textAlign: 'right'
                                                }}>
                                                    ↓ {step.dropoffRate}% no llegó
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div style={{
                                        marginTop: '8px',
                                        width: '100%',
                                        height: '6px',
                                        backgroundColor: '#e9ecef',
                                        borderRadius: '3px',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: `${step.completionRate}%`,
                                            height: '100%',
                                            backgroundColor: step.completionRate >= 50 ? '#27ae60' : step.completionRate >= 25 ? '#f39c12' : '#e74c3c',
                                            borderRadius: '3px',
                                            transition: 'width 0.3s ease'
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Biggest Dropoff Alert */}
                        {funnelMetrics.summary?.biggestDropoff?.rate > 0 && (
                            <div style={{
                                marginTop: isMobile ? '15px' : '20px',
                                backgroundColor: '#fff3cd',
                                border: '1px solid #ffc107',
                                padding: isMobile ? '12px' : '15px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '10px'
                            }}>
                                <span style={{ fontSize: isMobile ? '18px' : '20px' }}>⚠️</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontWeight: '700',
                                        color: '#856404',
                                        fontSize: isMobile ? '13px' : '14px',
                                        marginBottom: '4px'
                                    }}>
                                        Mayor Abandono
                                    </div>
                                    <div style={{
                                        color: '#856404',
                                        fontSize: isMobile ? '12px' : '13px'
                                    }}>
                                        {funnelMetrics.summary.biggestDropoff.rate}% de usuarios no llegaron a <strong>{funnelMetrics.summary.biggestDropoff.name}</strong>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tasaciones Tab */}
            {activeTab === 'tasaciones' && (
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                        <h3 style={{ color: '#2c3e50', margin: 0, fontSize: '20px' }}>🏠 Últimas Tasaciones</h3>
                        {selectedTasaciones.length > 0 && (
                            <button
                                onClick={exportSelectedTasaciones}
                                style={{
                                    padding: '10px 20px',
                                    backgroundColor: '#27ae60',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                📥 Exportar ({selectedTasaciones.length})
                            </button>
                        )}
                    </div>
                    
                    {/* Search Bar */}
                    <div style={{ 
                        marginBottom: '20px', 
                        display: 'flex', 
                        gap: '10px',
                        alignItems: 'center'
                    }}>
                        <div style={{ 
                            flex: 1,
                            position: 'relative'
                        }}>
                            <input
                                type="text"
                                placeholder="🔍 Buscar por dirección..."
                                value={addressSearch}
                                onChange={(e) => setAddressSearch(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        fetchTasaciones(1);
                                    }
                                }}
                                style={{
                                    width: '100%',
                                    padding: '12px 15px',
                                    fontSize: '14px',
                                    border: '2px solid #e1e8ed',
                                    borderRadius: '8px',
                                    outline: 'none',
                                    transition: 'border-color 0.2s'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                            />
                        </div>
                        <button
                            onClick={() => fetchTasaciones(1)}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#667eea',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                transition: 'all 0.2s',
                                whiteSpace: 'nowrap'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#5568d3'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#667eea'}
                        >
                            Buscar
                        </button>
                        {addressSearch && (
                            <button
                                onClick={() => {
                                    setAddressSearch('');
                                    setTimeout(() => fetchTasaciones(1), 100);
                                }}
                                style={{
                                    padding: '12px 20px',
                                    backgroundColor: '#95a5a6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#7f8c8d'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#95a5a6'}
                            >
                                Limpiar
                            </button>
                        )}
                    </div>
                    {tasaciones.length > 0 ? (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '40px' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedTasaciones.length === tasaciones.length}
                                                    onChange={toggleSelectAll}
                                                    style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                />
                                            </th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Operación</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Rango de Precio</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>IP</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tasaciones.map((tasacion, index) => {
                                            const isSelected = selectedTasaciones.some(t => t._id === tasacion._id);
                                            return (
                                            <tr 
                                                key={index} 
                                                style={{ 
                                                    borderBottom: '1px solid #dee2e6', 
                                                    backgroundColor: isSelected ? '#e8f5e9' : 'transparent',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent';
                                                }}
                                            >
                                                <td 
                                                    style={{ padding: '12px', textAlign: 'center' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleTasacionSelection(tasacion)}
                                                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                                    />
                                                </td>
                                                <td 
                                                    style={{ padding: '12px', fontSize: '12px', color: '#7f8c8d' }}
                                                    onClick={() => openTasacionModal(tasacion)}
                                                >
                                                    {tasacion.timestamp ? new Date(tasacion.timestamp).toLocaleDateString('es-ES', { 
                                                        year: 'numeric',
                                                        month: 'short', 
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.address} onClick={() => openTasacionModal(tasacion)}>
                                                    {tasacion.address}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }} onClick={() => openTasacionModal(tasacion)}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        backgroundColor: tasacion.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                                        color: tasacion.operation === 'alquiler' ? '#1976d2' : '#7b1fa2'
                                                    }}>
                                                        {tasacion.operation === 'alquiler' ? '🏠 Alquiler' : '💰 Venta'}
                                                    </span>
                                                </td>
                                                <td style={{ 
                                                    padding: '12px', 
                                                    textAlign: 'center', 
                                                    color: '#27ae60', 
                                                    fontWeight: 'bold', 
                                                    fontSize: tasacion.operation === 'alquiler' ? '12px' : '13px',
                                                    minWidth: '200px',
                                                    lineHeight: '1.4'
                                                }} onClick={() => openTasacionModal(tasacion)}>
                                                    {tasacion.priceRange}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }} onClick={() => openTasacionModal(tasacion)}>
                                                    {tasacion.phone}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.email} onClick={() => openTasacionModal(tasacion)}>
                                                    {tasacion.email}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#7f8c8d', fontFamily: 'monospace' }} onClick={() => openTasacionModal(tasacion)}>
                                                    {tasacion.ip || 'N/A'}
                                                </td>
                                                <td 
                                                    style={{ padding: '12px', textAlign: 'center' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <select
                                                        value={tasacion.status || 'Pendiente'}
                                                        onChange={async (e) => {
                                                            const newStatus = e.target.value;
                                                            try {
                                                                const response = await fetch(`${apiBaseUrl}/api/admin/update-tasacion-status`, {
                                                                    method: 'POST',
                                                                    headers: {
                                                                        'Content-Type': 'application/json',
                                                                        'Authorization': `Bearer ${userToken}`
                                                                    },
                                                                    body: JSON.stringify({
                                                                        tasacionId: tasacion._id,
                                                                        status: newStatus
                                                                    })
                                                                });
                                                                
                                                                if (response.ok) {
                                                                    // Update local state
                                                                    setTasaciones(prev => prev.map(t => 
                                                                        t._id === tasacion._id ? { ...t, status: newStatus } : t
                                                                    ));
                                                                } else {
                                                                    alert('Error al actualizar el estado');
                                                                }
                                                            } catch (error) {
                                                                console.error('Error updating status:', error);
                                                                alert('Error al actualizar el estado');
                                                            }
                                                        }}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            border: '1px solid #dee2e6',
                                                            backgroundColor: tasacion.status === 'Whatsapp' ? '#dcf8c6' : 
                                                                           tasacion.status === 'Contactado' ? '#e3f2fd' : 
                                                                           '#fff3cd',
                                                            color: tasacion.status === 'Whatsapp' ? '#128C7E' : 
                                                                   tasacion.status === 'Contactado' ? '#1976d2' : 
                                                                   '#856404',
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            cursor: 'pointer',
                                                            width: '120px'
                                                        }}
                                                    >
                                                        <option value="Pendiente">Pendiente</option>
                                                        <option value="Contactado">Contactado</option>
                                                        <option value="Whatsapp">Whatsapp</option>
                                                    </select>
                                                </td>
                                            </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination Controls */}
                            {tasacionesPagination && tasacionesPagination.totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                                    <button
                                        onClick={() => fetchTasaciones(currentPage - 1)}
                                        disabled={!tasacionesPagination.hasPrevPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasPrevPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasPrevPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasPrevPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        ← Anterior
                                    </button>
                                    
                                    <span style={{ color: '#2c3e50', fontSize: '14px', fontWeight: 'bold' }}>
                                        Página {tasacionesPagination.currentPage} de {tasacionesPagination.totalPages}
                                        <span style={{ color: '#7f8c8d', fontSize: '12px', marginLeft: '8px' }}>
                                            ({tasacionesPagination.totalCount} total)
                                        </span>
                                    </span>
                                    
                                    <button
                                        onClick={() => fetchTasaciones(currentPage + 1)}
                                        disabled={!tasacionesPagination.hasNextPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasNextPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasNextPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasNextPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                            No hay tasaciones disponibles
                        </div>
                    )}
                </div>
            )}

            {/* Lead Sources Tab */}
            {activeTab === 'leadSources' && leadSources && (
                <div>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👥 Visitantes Totales</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{formatNumber(leadSources.totals.totalVisitors)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Visitantes únicos</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>✅ Conversiones</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{formatNumber(leadSources.totals.totalConversions)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Tasaciones completadas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👤 Usuarios Únicos</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e67e22' }}>{formatNumber(leadSources.totals.totalUniqueUserConversions || 0)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Conversiones únicas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>📊 Tasa de Conversión</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{leadSources.totals.avgConversionRate}%</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Promedio general</div>
                        </div>
                    </div>

                    {/* Lead Sources Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>🎯 Fuentes de Leads por Performance</h3>
                        {leadSources.leadSources && leadSources.leadSources.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Referencia</th>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Fuente</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Visitantes</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Conversiones</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Usuarios Únicos</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Tasa Conv.</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Páginas/Visita</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leadSources.leadSources.map((source, index) => {
                                            // Source name formatting with emoji
                                            const getSourceDisplay = (sourceName) => {
                                                const sourceMap = {
                                                    'google_ads': '🎯 Google Ads',
                                                    'tiktok_ads': '🎵 TikTok Ads',
                                                    'facebook_ads': '👥 Facebook Ads',
                                                    'instagram_ads': '📷 Instagram Ads',
                                                    'google_organic': '🔍 Google Orgánico',
                                                    'facebook_organic': '👥 Facebook Orgánico',
                                                    'instagram_organic': '📷 Instagram Orgánico',
                                                    'tiktok_organic': '🎵 TikTok Orgánico',
                                                    'youtube': '📺 YouTube',
                                                    'referral': '🔗 Referencia',
                                                    'organic': '🌐 Directo/Orgánico',
                                                    'unknown': '❓ Desconocido'
                                                };
                                                return sourceMap[sourceName] || `📌 ${sourceName}`;
                                            };

                                            // Get clean reference name (platform only)
                                            const getReference = (sourceName) => {
                                                const referenceMap = {
                                                    'google_ads': 'Google',
                                                    'tiktok_ads': 'TikTok',
                                                    'facebook_ads': 'Facebook',
                                                    'instagram_ads': 'Instagram',
                                                    'google_organic': 'Google',
                                                    'facebook_organic': 'Facebook',
                                                    'instagram_organic': 'Instagram',
                                                    'tiktok_organic': 'TikTok',
                                                    'youtube': 'YouTube',
                                                    'referral': 'Referencia',
                                                    'organic': 'Directo',
                                                    'unknown': 'Desconocido'
                                                };
                                                return referenceMap[sourceName] || sourceName;
                                            };

                                            const convRate = source.conversionRate || 0;
                                            const convRateColor = convRate > 10 ? '#27ae60' : convRate > 5 ? '#f39c12' : '#e74c3c';

                                            return (
                                                <tr key={index} style={{ borderBottom: '1px solid #dee2e6', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                    <td style={{ padding: '15px', fontWeight: '700', color: '#2c3e50', fontSize: '16px' }}>{getReference(source.source)}</td>
                                                    <td style={{ padding: '15px', fontWeight: '600', color: '#7f8c8d' }}>{getSourceDisplay(source.source)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#3498db', fontWeight: '600' }}>{formatNumber(source.visitors)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#27ae60', fontWeight: '600' }}>{formatNumber(source.conversions)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#e67e22', fontWeight: '600' }}>{formatNumber(source.uniqueUserConversions || 0)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', color: convRateColor }}>
                                                        {convRate}%
                                                    </td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#7f8c8d' }}>{source.avgPageViews}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                No hay datos de fuentes de leads disponibles
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
                <div>
                    {/* Appointment Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Total Citas</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Pendientes</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>{appointments.filter(apt => apt.status === 'pending').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Contactados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.filter(apt => apt.status === 'contacted').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Completados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{appointments.filter(apt => apt.status === 'completed').length}</div>
                        </div>
                    </div>

                    {/* Appointments Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>📅 Citas con Martillero</h3>
                        
                        {appointmentsLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                                ⏳ Cargando citas...
                            </div>
                        ) : appointments.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '150px' }}>👤 Cliente</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '120px' }}>📅 Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '100px' }}>🕒 Hora</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '200px' }}>📍 Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '120px' }}>📞 Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '180px' }}>💰 Valuación</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>📅 Creada</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appointments.map((apt, index) => {
                                            const statusColors = {
                                                pending: '#f39c12',
                                                contacted: '#3498db',
                                                completed: '#27ae60',
                                                cancelled: '#e74c3c'
                                            };
                                            const statusLabels = {
                                                pending: 'Pendiente',
                                                contacted: 'Contactado',
                                                completed: 'Completado',
                                                cancelled: 'Cancelado'
                                            };
                                            
                                            return (
                                                <tr key={apt._id} style={{ borderBottom: '1px solid #e1e8ed', backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                                                    <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600' }}>{apt.clientName || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.date || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.time || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '13px' }}>{apt.address || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                                        {apt.phone && apt.phone !== 'N/A' ? (
                                                            <a 
                                                                href={`https://wa.me/${apt.phone?.replace(/\D/g, '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ color: '#25D366', fontWeight: 'bold', textDecoration: 'none' }}
                                                            >
                                                                {apt.phone}
                                                            </a>
                                                        ) : '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '13px', color: '#27ae60', fontWeight: '600' }}>
                                                        {apt.priceRange || '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '13px', color: '#7f8c8d' }}>{formatDate(apt.createdAt)}</td>
                                                    <td style={{ padding: '12px' }}>
                                                        <select
                                                            value={apt.status || 'pending'}
                                                            onChange={(e) => updateAppointmentStatus(apt._id, e.target.value)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                border: '2px solid #e1e8ed',
                                                                backgroundColor: statusColors[apt.status || 'pending'] || '#95a5a6',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '13px',
                                                                cursor: 'pointer',
                                                                width: '130px'
                                                            }}
                                                        >
                                                            <option value="pending">Pendiente</option>
                                                            <option value="contacted">Contactado</option>
                                                            <option value="completed">Completado</option>
                                                            <option value="cancelled">Cancelado</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                📅 No hay citas registradas todavía
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Messages Tab - WhatsApp Interface */}
            {activeTab === 'messages' && (
                <>
                {/* New Message Notification */}
                {newMessageNotification && (
                    <div style={{
                        position: 'fixed',
                        top: '100px',
                        right: '20px',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '15px 20px',
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                        zIndex: 9999,
                        maxWidth: '350px',
                        animation: 'slideIn 0.3s ease-out'
                    }}>
                        <style>
                            {`
                                @keyframes slideIn {
                                    from {
                                        transform: translateX(400px);
                                        opacity: 0;
                                    }
                                    to {
                                        transform: translateX(0);
                                        opacity: 1;
                                    }
                                }
                            `}
                        </style>
                        <div style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '14px' }}>
                            💬 Nuevo mensaje de {newMessageNotification.phone}
                        </div>
                        <div style={{ fontSize: '13px', opacity: 0.9 }}>
                            {newMessageNotification.message.substring(0, 80)}{newMessageNotification.message.length > 80 ? '...' : ''}
                        </div>
                    </div>
                )}
                
                <div style={{ 
                    backgroundColor: 'white', 
                    borderRadius: '0', 
                    boxShadow: 'none', 
                    overflow: 'hidden',
                    height: isMobile && showMobileConversation ? '100vh' : 'calc(100vh - 80px)', // Full viewport on mobile chat, minus header otherwise
                    display: 'flex',
                    width: '100%'
                }}>
                    {/* Left Side - Conversations List */}
                    <div style={{ 
                        width: isMobile ? '100%' : '380px', 
                        height: isMobile ? 'calc(100vh - 60px)' : 'auto', // Account for browser footer
                        borderRight: isMobile ? 'none' : '1px solid #e9ecef',
                        display: (isMobile && showMobileConversation) ? 'none' : 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#f8f9fa',
                        overflow: 'hidden',
                        position: isMobile ? 'fixed' : 'relative',
                        top: isMobile ? 0 : 'auto',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto'
                    }}>
                        {/* Header */}
                        <div style={{ 
                            padding: isMobile ? '15px' : '20px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            borderBottom: '1px solid rgba(0,0,0,0.1)'
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                marginBottom: '15px',
                                gap: '10px'
                            }}>
                                {/* Left: Hamburger menu on mobile, emoji on desktop */}
                                {isMobile ? (
                                    <button
                                        onClick={() => {
                                            // Toggle sidebar on mobile
                                            const event = new CustomEvent('toggle-sidebar');
                                            window.dispatchEvent(event);
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'white',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            padding: '0',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '40px',
                                            height: '40px'
                                        }}
                                    >
                                        ☰
                                    </button>
                                ) : (
                                    <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
                                        💬 Conversaciones
                                    </h3>
                                )}
                                
                                {/* Center: Title on mobile with logo */}
                                {isMobile && (
                                    <div style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        flex: 1,
                                        justifyContent: 'center'
                                    }}>
                                        <img 
                                            src={logoMain} 
                                            alt="Logo" 
                                            style={{ 
                                                height: '28px',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </div>
                                )}
                                
                                {/* Right: Connection status indicator */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: isSocketConnected ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                    padding: isMobile ? '5px 10px' : '6px 12px',
                                    borderRadius: '20px',
                                    fontSize: isMobile ? '11px' : '13px',
                                    fontWeight: '600'
                                }}>
                                    <span style={{
                                        width: '8px',
                                        height: '8px',
                                        backgroundColor: isSocketConnected ? '#00ff00' : '#ff4444',
                                        borderRadius: '50%',
                                        display: 'inline-block',
                                        animation: isSocketConnected ? 'pulse 2s ease-in-out infinite' : 'none'
                                    }}></span>
                                    <style>
                                        {`
                                            @keyframes pulse {
                                                0%, 100% { opacity: 1; }
                                                50% { opacity: 0.4; }
                                            }
                                        `}
                                    </style>
                                    {isSocketConnected ? 'Online' : 'Offline'}
                                </div>
                            </div>
                            {/* Search */}
                            <input 
                                type="text"
                                placeholder="Buscar conversación..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '10px 15px',
                                    border: 'none',
                                    borderRadius: '20px',
                                    fontSize: '14px',
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    outline: 'none'
                                }}
                            />
                        </div>

                        {/* Conversations List */}
                        <div style={{ 
                            flex: 1, 
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            WebkitOverflowScrolling: 'touch',
                            backgroundColor: 'white',
                            height: isMobile ? 'calc(100vh - 200px)' : 'auto', // viewport - header (with search) - browser chrome
                            paddingBottom: isMobile ? '60px' : '0' // Extra padding for browser footer
                        }}>
                            {conversationsLoading ? (
                                <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                                    Cargando conversaciones...
                                </div>
                            ) : filteredConversations.length > 0 ? (
                                filteredConversations.map((conv) => (
                                    <div
                                        key={conv._id || conv.phoneNumber}
                                        onClick={() => selectConversation(conv)}
                                        style={{
                                            padding: '16px 20px',
                                            borderBottom: '1px solid #f0f0f0',
                                            cursor: 'pointer',
                                            backgroundColor: selectedConversation?.phoneNumber === conv.phoneNumber ? '#e7f3ff' : 'white',
                                            transition: 'background-color 0.2s',
                                            display: 'flex',
                                            gap: '12px',
                                            alignItems: 'flex-start'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (selectedConversation?.phoneNumber !== conv.phoneNumber) {
                                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (selectedConversation?.phoneNumber !== conv.phoneNumber) {
                                                e.currentTarget.style.backgroundColor = 'white';
                                            }
                                        }}
                                    >
                                        {/* Avatar */}
                                        <div style={{
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            flexShrink: 0
                                        }}>
                                            {conv.name ? conv.name.charAt(0).toUpperCase() : '👤'}
                                        </div>

                                        {/* Conversation Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: '4px'
                                            }}>
                                                <div style={{ 
                                                    fontWeight: conv.unreadCount > 0 ? '700' : '600',
                                                    fontSize: '15px',
                                                    color: '#2c3e50',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}>
                                                    {conv.name || conv.phoneNumber}
                                                </div>
                                                <div style={{ 
                                                    fontSize: '12px',
                                                    color: '#7f8c8d',
                                                    flexShrink: 0,
                                                    marginLeft: '8px'
                                                }}>
                                                    {formatMessageTime(conv.lastMessageTime)}
                                                </div>
                                            </div>
                                            <div style={{ fontSize: '13px', color: '#7f8c8d', marginBottom: '4px' }}>
                                                +{conv.phoneNumber}
                                            </div>
                                            {conv.address && typeof conv.address === 'string' && (
                                                <div style={{ 
                                                    fontSize: '12px', 
                                                    color: '#00a884',
                                                    marginBottom: '6px',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}>
                                                    <span>📍</span>
                                                    <span style={{ 
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {conv.address}
                                                    </span>
                                                </div>
                                            )}
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                <div style={{
                                                    fontSize: '14px',
                                                    color: conv.unreadCount > 0 ? '#2c3e50' : '#95a5a6',
                                                    fontWeight: conv.unreadCount > 0 ? '600' : '400',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    flex: 1
                                                }}>
                                                    {conv.lastMessage || 'Sin mensajes'}
                                                </div>
                                                {conv.unreadCount > 0 && (
                                                    <div style={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        borderRadius: '12px',
                                                        padding: '2px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        marginLeft: '8px',
                                                        flexShrink: 0
                                                    }}>
                                                        {conv.unreadCount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div style={{ 
                                    padding: '60px 20px', 
                                    textAlign: 'center',
                                    color: '#95a5a6'
                                }}>
                                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>💬</div>
                                    <div style={{ fontSize: '16px', fontWeight: '500' }}>
                                        {searchQuery ? 'No se encontraron conversaciones' : 'No hay conversaciones todavía'}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Side - Chat Area */}
                    <div style={{ 
                        flex: 1,
                        width: isMobile ? '100%' : 'auto',
                        height: isMobile ? 'calc(100vh - 60px)' : 'auto', // Account for browser footer
                        maxHeight: isMobile ? 'calc(100vh - 60px)' : 'none',
                        display: (isMobile && !showMobileConversation) ? 'none' : 'flex',
                        flexDirection: 'column',
                        backgroundColor: '#e5ddd5',
                        overflow: 'hidden', // Prevent page scroll
                        position: isMobile ? 'fixed' : 'relative',
                        top: isMobile ? 0 : 'auto',
                        left: isMobile ? 0 : 'auto',
                        right: isMobile ? 0 : 'auto'
                    }}>
                        {selectedConversation ? (
                            <>
                                {/* Chat Header */}
                                <div style={{
                                    padding: '16px 20px',
                                    backgroundColor: '#f0f2f5',
                                    borderBottom: '1px solid #d1d7db',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 10
                                }}>
                                    {/* Back button - Mobile only */}
                                    {isMobile && (
                                        <button
                                            onClick={handleBackToConversations}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                fontSize: '24px',
                                                cursor: 'pointer',
                                                padding: '0',
                                                color: '#54656f',
                                                display: 'flex',
                                                alignItems: 'center',
                                                marginRight: '8px'
                                            }}
                                        >
                                            ←
                                        </button>
                                    )}
                                    
                                    <div style={{
                                        width: '42px',
                                        height: '42px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '18px',
                                        fontWeight: 'bold'
                                    }}>
                                        {selectedConversation.name ? selectedConversation.name.charAt(0).toUpperCase() : '👤'}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '600', fontSize: '16px', color: '#2c3e50', textAlign: 'center' }}>
                                            {selectedConversation.name || selectedConversation.phoneNumber}
                                        </div>
                                        <div style={{ fontSize: '13px', color: '#667781', textAlign: 'center' }}>
                                            +{selectedConversation.phoneNumber}
                                        </div>
                                        {selectedConversation.address && typeof selectedConversation.address === 'string' && (
                                            <div style={{ 
                                                fontSize: '12px', 
                                                color: '#00a884',
                                                marginTop: '4px',
                                                textAlign: 'center'
                                            }}>
                                                <span>📍 {selectedConversation.address}</span>
                                            </div>
                                        )}
                                    </div>
                                    {/* Copy Details Button */}
                                    <button
                                        id="copy-details-btn"
                                        onClick={copyConversationDetails}
                                        title="Copiar detalles del contacto"
                                        style={{
                                            background: 'transparent',
                                            border: '1px solid #d1d7db',
                                            borderRadius: '8px',
                                            padding: '8px 12px',
                                            cursor: 'pointer',
                                            fontSize: '16px',
                                            color: '#54656f',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '4px',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#e9ecef';
                                            e.currentTarget.style.borderColor = '#00a884';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'transparent';
                                            e.currentTarget.style.borderColor = '#d1d7db';
                                        }}
                                    >
                                        📋
                                    </button>
                                </div>

                                {/* Messages Area */}
                                <div style={{
                                    flex: 1,
                                    overflowY: 'auto',
                                    overflowX: 'hidden',
                                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                                    padding: '20px',
                                    paddingBottom: isMobile ? '140px' : '20px', // Extra padding for fixed input on mobile (increased)
                                    height: isMobile ? 'auto' : 'auto',
                                    marginBottom: isMobile ? '80px' : '0', // Space for fixed input
                                    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M0 0h100v100H0z\' fill=\'%23e5ddd5\'/%3E%3Cpath d=\'M20 20h60v60H20z\' fill=\'none\' stroke=\'%23d1d7db\' stroke-width=\'0.5\' opacity=\'0.1\'/%3E%3C/svg%3E")'
                                }}>
                                    {messagesLoading ? (
                                        <div style={{ textAlign: 'center', color: '#667781', paddingTop: '40px' }}>
                                            Cargando mensajes...
                                        </div>
                                    ) : messages.length > 0 ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                            {messages.map((msg, index) => {
                                                const isFromAdmin = msg.direction === 'outgoing';
                                                
                                                // DEBUG: Log audio messages to console
                                                if (msg.type === 'audio' || msg.content?.includes('🎤 Audio') || msg.mediaType?.includes('audio')) {
                                                    console.log('🎵 Audio message detected:', {
                                                        messageId: msg.messageId,
                                                        _id: msg._id,
                                                        type: msg.type,
                                                        mediaType: msg.mediaType,
                                                        hasMediaUrl: !!msg.mediaUrl,
                                                        mediaUrlPreview: msg.mediaUrl?.substring(0, 60),
                                                        content: msg.content
                                                    });
                                                }
                                                
                                                return (
                                                    <div
                                                        key={msg._id || msg.messageId || index}
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: isFromAdmin ? 'flex-end' : 'flex-start',
                                                            marginBottom: '4px'
                                                        }}
                                                    >
                                                        <div style={{
                                                            maxWidth: '65%',
                                                            backgroundColor: isFromAdmin ? '#d9fdd3' : 'white',
                                                            borderRadius: '8px',
                                                            padding: '8px 12px',
                                                            boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                                            position: 'relative'
                                                        }}>
                                                            {/* Message Content - Check for Audio/Media */}
                                                            {(() => {
                                                                // Detect audio messages in multiple ways
                                                                const isAudio = msg.type === 'audio' || 
                                                                               msg.mediaType?.includes('audio') || 
                                                                               msg.mediaType?.includes('ogg') ||
                                                                               msg.content?.includes('🎤 Audio') ||
                                                                               (msg.mediaUrl && (
                                                                                   msg.mediaUrl.includes('audio') ||
                                                                                   msg.mediaUrl.includes('.ogg') ||
                                                                                   msg.mediaUrl.includes('.mp3') ||
                                                                                   msg.mediaUrl.includes('.m4a') ||
                                                                                   msg.mediaUrl.includes('.wav')
                                                                               ));
                                                                return isAudio;
                                                            })() ? (
                                                                // Audio Message - Show Audio Player
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <div style={{
                                                                        fontSize: '13px',
                                                                        color: '#667781',
                                                                        marginBottom: '8px',
                                                                        fontWeight: '500',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '6px'
                                                                    }}>
                                                                        <span style={{
                                                                            fontSize: '16px',
                                                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                            borderRadius: '50%',
                                                                            width: '24px',
                                                                            height: '24px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            color: 'white'
                                                                        }}>🎤</span>
                                                                        <span>Mensaje de Audio</span>
                                                                    </div>
                                                                    {(msg.messageId || msg._id) ? (
                                                                        <div style={{ 
                                                                            background: 'linear-gradient(135deg, #f5f7fa 0%, #e3e7ef 100%)',
                                                                            borderRadius: '12px',
                                                                            padding: '12px',
                                                                            border: '2px solid #667eea',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '12px',
                                                                            cursor: 'pointer',
                                                                            position: 'relative',
                                                                            minHeight: '60px'
                                                                        }}
                                                                        onClick={(e) => {
                                                                            const audio = e.currentTarget.querySelector('audio');
                                                                            if (audio.paused) {
                                                                                audio.play();
                                                                            } else {
                                                                                audio.pause();
                                                                            }
                                                                        }}>
                                                                            {console.log('🎧 Rendering audio player for:', msg.messageId || msg._id)}
                                                                            
                                                                            {/* Big Play Button */}
                                                                            <div style={{
                                                                                width: '48px',
                                                                                height: '48px',
                                                                                borderRadius: '50%',
                                                                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                color: 'white',
                                                                                fontSize: '24px',
                                                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                                                                flexShrink: 0,
                                                                                transition: 'transform 0.2s',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                                                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                                                            >
                                                                                ▶️
                                                                            </div>
                                                                            
                                                                            {/* Audio info */}
                                                                            <div style={{ 
                                                                                flex: 1,
                                                                                fontSize: '14px',
                                                                                color: '#667781',
                                                                                fontWeight: '500'
                                                                            }}>
                                                                                Presioná para escuchar
                                                                            </div>
                                                                            
                                                                            {/* Hidden native audio for functionality */}
                                                                            <audio 
                                                                                key={msg.messageId || msg._id}
                                                                                src={`${apiBaseUrl}/api/whatsapp/media/${msg.messageId || msg._id}?token=${encodeURIComponent(userToken)}`}
                                                                                style={{ display: 'none' }}
                                                                                preload="none"
                                                                                onError={(e) => {
                                                                                    console.error('🔴 Audio load error:', {
                                                                                        messageId: msg.messageId || msg._id,
                                                                                        src: `/api/whatsapp/media/${msg.messageId || msg._id}`,
                                                                                        currentSrc: e.target.currentSrc,
                                                                                        error: e.target.error,
                                                                                        networkState: e.target.networkState,
                                                                                        readyState: e.target.readyState
                                                                                    });
                                                                                }}
                                                                                onLoadStart={(e) => {
                                                                                    console.log('▶️ Audio load started:', {
                                                                                        messageId: msg.messageId || msg._id,
                                                                                        src: e.target.currentSrc
                                                                                    });
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    
                                                                    ) : (
                                                                        <div style={{ fontSize: '12px', color: '#e74c3c', fontStyle: 'italic' }}>
                                                                            ⚠️ Audio detectado pero no se puede cargar
                                                                            <br/>
                                                                            <span style={{ fontSize: '10px' }}>
                                                                                ID: {msg.messageId || msg._id || 'none'} | 
                                                                                URL: {msg.mediaUrl ? 'Sí' : 'No'} | 
                                                                                Type: {msg.type || 'none'} | 
                                                                                MediaType: {msg.mediaType || 'none'}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : msg.type === 'image' || msg.mediaType?.includes('image') ? (
                                                                // Image Message - Show Image
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <div style={{
                                                                        fontSize: '13px',
                                                                        color: '#667781',
                                                                        marginBottom: '6px',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        📷 Imagen
                                                                    </div>
                                                                    {msg.mediaUrl && (msg.messageId || msg._id) ? (
                                                                        <img 
                                                                            src={`/api/whatsapp/media/${msg.messageId || msg._id}`} 
                                                                            alt="Imagen enviada"
                                                                            style={{
                                                                                maxWidth: '250px',
                                                                                maxHeight: '300px',
                                                                                borderRadius: '8px',
                                                                                cursor: 'pointer'
                                                                            }}
                                                                            onClick={() => window.open(`/api/whatsapp/media/${msg.messageId || msg._id}`, '_blank')}
                                                                        />
                                                                    ) : (
                                                                        <div style={{ fontSize: '12px', color: '#95a5a6', fontStyle: 'italic' }}>
                                                                            Imagen no disponible
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : msg.type === 'video' || msg.mediaType?.includes('video') ? (
                                                                // Video Message - Show Video Player
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <div style={{
                                                                        fontSize: '13px',
                                                                        color: '#667781',
                                                                        marginBottom: '6px',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        🎥 Video
                                                                    </div>
                                                                    {msg.mediaUrl && (msg.messageId || msg._id) ? (
                                                                        <video 
                                                                            controls 
                                                                            style={{ 
                                                                                maxWidth: '250px',
                                                                                maxHeight: '300px',
                                                                                borderRadius: '8px'
                                                                            }}
                                                                            preload="metadata"
                                                                        >
                                                                            <source src={`/api/whatsapp/media/${msg.messageId || msg._id}`} type={msg.mediaType || 'video/mp4'} />
                                                                            Tu navegador no soporta reproducción de video.
                                                                        </video>
                                                                    ) : (
                                                                        <div style={{ fontSize: '12px', color: '#95a5a6', fontStyle: 'italic' }}>
                                                                            Video no disponible
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : msg.type === 'document' || msg.mediaType?.includes('document') || msg.mediaType?.includes('pdf') ? (
                                                                // Document Message - Show Download Link
                                                                <div style={{ marginBottom: '4px' }}>
                                                                    <div style={{
                                                                        fontSize: '13px',
                                                                        color: '#667781',
                                                                        marginBottom: '6px',
                                                                        fontWeight: '500'
                                                                    }}>
                                                                        📄 Documento
                                                                    </div>
                                                                    {msg.mediaUrl && (msg.messageId || msg._id) ? (
                                                                        <a 
                                                                            href={`/api/whatsapp/media/${msg.messageId || msg._id}`} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                color: '#667eea',
                                                                                textDecoration: 'underline',
                                                                                fontSize: '13px'
                                                                            }}
                                                                        >
                                                                            Descargar documento
                                                                        </a>
                                                                    ) : (
                                                                        <div style={{ fontSize: '12px', color: '#95a5a6', fontStyle: 'italic' }}>
                                                                            Documento no disponible
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                // Text Message - Show Normal Text
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    color: '#2c3e50',
                                                                    wordWrap: 'break-word',
                                                                    marginBottom: '4px'
                                                                }}>
                                                                    {msg.content}
                                                                </div>
                                                            )}
                                                            
                                                            {/* Timestamp and Status */}
                                                            <div style={{
                                                                fontSize: '11px',
                                                                color: '#667781',
                                                                textAlign: 'right',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'flex-end',
                                                                gap: '4px'
                                                            }}>
                                                                {new Date(msg.timestamp).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                                                                {isFromAdmin && (
                                                                    <span style={{ color: msg.status === 'read' ? '#53bdeb' : msg.status === 'delivered' ? '#95a5a6' : '#95a5a6' }}>
                                                                        {msg.status === 'read' ? '✓✓' : msg.status === 'delivered' ? '✓✓' : '✓'}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {/* Invisible element for auto-scroll */}
                                            <div ref={messagesEndRef} />
                                        </div>
                                    ) : (
                                        <div style={{ 
                                            textAlign: 'center', 
                                            color: '#667781',
                                            paddingTop: '60px'
                                        }}>
                                            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
                                            <div>No hay mensajes en esta conversación</div>
                                        </div>
                                    )}
                                </div>

                                {/* Message Input */}
                                <div style={{
                                    padding: '12px 16px',
                                    paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom) + 12px)' : '12px', // Account for iOS safe area
                                    backgroundColor: '#f0f2f5',
                                    borderTop: '1px solid #d1d7db',
                                    position: isMobile ? 'fixed' : 'sticky',
                                    bottom: 0,
                                    left: isMobile ? 0 : 'auto',
                                    right: isMobile ? 0 : 'auto',
                                    width: isMobile ? '100%' : 'auto',
                                    boxSizing: 'border-box',
                                    zIndex: 1000
                                }}>
                                    {selectedTemplate && (
                                        <div style={{
                                            padding: '8px 12px',
                                            backgroundColor: '#dcf8c6',
                                            borderRadius: '8px',
                                            marginBottom: '8px',
                                            fontSize: '13px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <span>📋 Template: {selectedTemplate.name}</span>
                                            <button
                                                onClick={() => {
                                                    setSelectedTemplate(null);
                                                    setTemplateParameters([]);
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#666',
                                                    cursor: 'pointer',
                                                    fontSize: '16px'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    )}
                                    <div style={{
                                        display: 'flex',
                                        gap: '8px',
                                        alignItems: 'center'
                                    }}>
                                        <button
                                            onClick={() => setShowTemplates(!showTemplates)}
                                            style={{
                                                padding: '12px',
                                                background: showTemplates ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'white',
                                                color: showTemplates ? 'white' : '#667781',
                                                border: 'none',
                                                borderRadius: '50%',
                                                cursor: 'pointer',
                                                fontSize: '18px',
                                                width: '44px',
                                                height: '44px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                            title="Plantillas"
                                        >
                                            📋
                                        </button>
                                        <input
                                            type="text"
                                            placeholder={selectedTemplate ? "Presiona enviar para usar plantilla" : "Escribe un mensaje..."}
                                            value={messageInput}
                                            onChange={(e) => setMessageInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage();
                                                }
                                            }}
                                            disabled={!!selectedTemplate}
                                            style={{
                                                flex: 1,
                                                padding: '12px 16px',
                                                border: 'none',
                                                borderRadius: '24px',
                                                fontSize: '15px',
                                                outline: 'none',
                                                backgroundColor: selectedTemplate ? '#f5f5f5' : 'white',
                                                opacity: selectedTemplate ? 0.7 : 1
                                            }}
                                        />
                                        <button
                                            onClick={sendMessage}
                                            disabled={!selectedTemplate && !messageInput.trim()}
                                            style={{
                                                padding: '12px 24px',
                                                background: (selectedTemplate || messageInput.trim()) ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#ccc',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '24px',
                                                cursor: (selectedTemplate || messageInput.trim()) ? 'pointer' : 'not-allowed',
                                                fontSize: '15px',
                                                fontWeight: '600',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                if (selectedTemplate || messageInput.trim()) {
                                                    e.target.style.transform = 'scale(1.05)';
                                                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedTemplate || messageInput.trim()) {
                                                    e.target.style.transform = 'scale(1)';
                                                    e.target.style.boxShadow = 'none';
                                                }
                                            }}
                                        >
                                            Enviar
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Templates Modal */}
                                {showTemplates && (
                                    <div style={{
                                        position: 'absolute',
                                        bottom: '80px',
                                        left: '16px',
                                        right: '16px',
                                        maxHeight: '400px',
                                        backgroundColor: 'white',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                                        overflow: 'hidden',
                                        zIndex: 1000
                                    }}>
                                        <div style={{
                                            padding: '16px',
                                            backgroundColor: '#f0f2f5',
                                            borderBottom: '1px solid #d1d7db',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}>
                                            <h4 style={{ margin: 0, fontSize: '16px' }}>📋 Plantillas Disponibles</h4>
                                            <button
                                                onClick={() => setShowTemplates(false)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    fontSize: '20px',
                                                    cursor: 'pointer',
                                                    color: '#666'
                                                }}
                                            >
                                                ✕
                                            </button>
                                        </div>
                                        <div style={{
                                            maxHeight: '340px',
                                            overflowY: 'auto',
                                            padding: '8px'
                                        }}>
                                            {templates.length > 0 ? (
                                                templates.map((template, index) => (
                                                    <div
                                                        key={template.id || index}
                                                        onClick={() => {
                                                            setSelectedTemplate(template);
                                                            setShowTemplates(false);
                                                        }}
                                                        style={{
                                                            padding: '12px',
                                                            margin: '8px',
                                                            backgroundColor: '#f8f9fa',
                                                            borderRadius: '8px',
                                                            cursor: 'pointer',
                                                            border: '1px solid #e1e8ed',
                                                            transition: 'all 0.2s'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#f3f0ff';
                                                            e.currentTarget.style.borderColor = '#667eea';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                            e.currentTarget.style.borderColor = '#e1e8ed';
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>
                                                            {template.name}
                                                        </div>
                                                        <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '8px' }}>
                                                            {template.language} • {template.category}
                                                        </div>
                                                        {template.components?.map((comp, idx) => (
                                                            comp.type === 'BODY' && (
                                                                <div key={idx} style={{ fontSize: '13px', color: '#2c3e50' }}>
                                                                    {comp.text}
                                                                </div>
                                                            )
                                                        ))}
                                                    </div>
                                                ))
                                            ) : (
                                                <div style={{ padding: '40px', textAlign: 'center', color: '#7f8c8d' }}>
                                                    <div style={{ fontSize: '40px', marginBottom: '12px' }}>📋</div>
                                                    <div>No hay plantillas disponibles</div>
                                                    <div style={{ fontSize: '12px', marginTop: '8px' }}>
                                                        Crea plantillas en tu Meta Business Manager
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%',
                                color: '#667781',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <div style={{ fontSize: '72px', marginBottom: '20px' }}>💬</div>
                                <div style={{ fontSize: '20px', fontWeight: '500', marginBottom: '8px' }}>
                                    WhatsApp Business
                                </div>
                                <div style={{ fontSize: '14px', color: '#95a5a6' }}>
                                    Selecciona una conversación para comenzar
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                </>
            )}

            {/* Settings Tab - Admin Only */}
            {activeTab === 'settings' && (
                <div style={{ 
                    backgroundColor: 'white', 
                    padding: isMobile ? '15px' : '30px', 
                    borderRadius: isMobile ? '8px' : '12px', 
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    maxWidth: '800px',
                    margin: '0 auto'
                }}>
                    <h2 style={{ 
                        fontSize: isMobile ? '20px' : '24px', 
                        fontWeight: '700', 
                        color: '#2c3e50', 
                        marginBottom: isMobile ? '20px' : '30px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '8px' : '12px',
                        flexWrap: 'wrap'
                    }}>
                        ⚙️ Ajustes del Sistema
                        <span style={{
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: '600',
                            color: '#e74c3c',
                            backgroundColor: '#ffe5e5',
                            padding: isMobile ? '3px 8px' : '4px 10px',
                            borderRadius: '4px',
                            marginLeft: isMobile ? '0' : 'auto'
                        }}>
                            🔒 SOLO ADMIN
                        </span>
                    </h2>

                    {/* WhatsApp Verification Toggle */}
                    <div style={{ 
                        border: isMobile ? '1px solid #e1e8ed' : '2px solid #e1e8ed', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '25px',
                        marginBottom: isMobile ? '15px' : '20px',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: isMobile ? 'flex-start' : 'center',
                            flexWrap: 'wrap',
                            gap: isMobile ? '12px' : '15px',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '250px' }}>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    📱 Verificación de WhatsApp
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    margin: 0,
                                    lineHeight: '1.5'
                                }}>
                                    Cuando está activada, el sistema verifica que el número de teléfono tenga WhatsApp antes de permitir realizar una tasación.
                                    {!whatsappVerificationEnabled && (
                                        <span style={{ 
                                            display: 'block', 
                                            marginTop: '8px', 
                                            color: '#e67e22', 
                                            fontWeight: '600',
                                            fontSize: isMobile ? '12px' : '14px'
                                        }}>
                                            ⚠️ Desactivado: Los usuarios pueden tasar sin verificación de WhatsApp
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'space-between' : 'flex-start'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: whatsappVerificationEnabled ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {whatsappVerificationEnabled ? 'Activado' : 'Desactivado'}
                                </span>
                                <button
                                    onClick={toggleWhatsappVerification}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: whatsappVerificationEnabled ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: whatsappVerificationEnabled ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Auto-Send WhatsApp Template Toggle */}
                    <div style={{ 
                        border: isMobile ? '1px solid #e1e8ed' : '2px solid #e1e8ed', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '25px',
                        marginBottom: isMobile ? '15px' : '20px',
                        backgroundColor: '#f8f9fa'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: isMobile ? 'flex-start' : 'center',
                            flexWrap: 'wrap',
                            gap: isMobile ? '12px' : '15px',
                            flexDirection: isMobile ? 'column' : 'row'
                        }}>
                            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '250px' }}>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    💬 Envío Automático de Plantillas WhatsApp
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    margin: 0,
                                    lineHeight: '1.5'
                                }}>
                                    Envía automáticamente una plantilla de WhatsApp a usuarios después de completar una tasación. 
                                    <strong> Condiciones:</strong> No envía a inmobiliarias, solo una vez por usuario, espera 5 minutos, 
                                    y respeta horario (no envía después de 10 PM, espera hasta 8:30 AM del día siguiente).
                                    {autoSendWhatsAppTemplate && (
                                        <span style={{ 
                                            display: 'block', 
                                            marginTop: '8px', 
                                            color: '#27ae60', 
                                            fontWeight: '600',
                                            fontSize: isMobile ? '12px' : '14px'
                                        }}>
                                            ✅ Activado: Las plantillas se enviarán automáticamente
                                        </span>
                                    )}
                                    {!autoSendWhatsAppTemplate && (
                                        <span style={{ 
                                            display: 'block', 
                                            marginTop: '8px', 
                                            color: '#e67e22', 
                                            fontWeight: '600',
                                            fontSize: isMobile ? '12px' : '14px'
                                        }}>
                                            ⚠️ Desactivado: No se enviarán plantillas automáticamente
                                        </span>
                                    )}
                                </p>
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '12px',
                                width: isMobile ? '100%' : 'auto',
                                justifyContent: isMobile ? 'space-between' : 'flex-start'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: autoSendWhatsAppTemplate ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {autoSendWhatsAppTemplate ? 'Activado' : 'Desactivado'}
                                </span>
                                <button
                                    onClick={toggleAutoSendTemplate}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: autoSendWhatsAppTemplate ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: autoSendWhatsAppTemplate ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Phone Number Requirement Setting */}
                    <div style={{ 
                        backgroundColor: 'white', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '20px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        marginBottom: isMobile ? '15px' : '20px',
                        border: isMobile ? '1px solid #e1e8ed' : 'none'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '15px'
                        }}>
                            <div>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px' 
                                }}>
                                    📱 Requerir Número de Teléfono
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    marginBottom: '15px', 
                                    lineHeight: '1.6' 
                                }}>
                                    Cuando está <strong>activado</strong>, los usuarios deben ingresar su número de teléfono para ver la valuación.
                                    Cuando está <strong>desactivado</strong>, los usuarios pueden ver la valuación sin proporcionar su teléfono.
                                </p>
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: requirePhoneNumber ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {requirePhoneNumber ? 'Teléfono Requerido' : 'Teléfono Opcional'}
                                </span>
                                <button
                                    onClick={toggleRequirePhoneNumber}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: requirePhoneNumber ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: requirePhoneNumber ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tasacion Completion Email Setting */}
                    <div style={{ 
                        backgroundColor: 'white', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '20px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        marginBottom: isMobile ? '15px' : '20px',
                        border: isMobile ? '1px solid #e1e8ed' : 'none'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '15px'
                        }}>
                            <div>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px' 
                                }}>
                                    📧 Enviar Email al Completar Tasación
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    marginBottom: '15px', 
                                    lineHeight: '1.6' 
                                }}>
                                    Cuando está <strong>activado</strong>, se enviará un email con los detalles de la tasación completada (solo la primera vez por usuario).
                                    Cuando está <strong>desactivado</strong>, no se enviarán emails de tasación completada.
                                    <br/><em style={{ fontSize: isMobile ? '12px' : '13px', color: '#95a5a6' }}>Nota: Los emails de reservas de martillero no se ven afectados.</em>
                                </p>
                            </div>
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: sendTasacionCompletionEmails ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {sendTasacionCompletionEmails ? 'Emails Activados' : 'Emails Desactivados'}
                                </span>
                                <button
                                    onClick={toggleSendTasacionCompletionEmails}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: sendTasacionCompletionEmails ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: sendTasacionCompletionEmails ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Enable Pay Option Setting */}
                    <div style={{ 
                        backgroundColor: 'white', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '20px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        marginBottom: isMobile ? '15px' : '20px',
                        border: isMobile ? '1px solid #e1e8ed' : 'none'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '15px'
                        }}>
                            <div>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    💳 Opción de Pago
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    marginBottom: '15px', 
                                    lineHeight: '1.6' 
                                }}>
                                    Cuando está <strong>activado</strong>, los usuarios podrán acceder a la opción de pago.
                                    Cuando está <strong>desactivado</strong>, la opción de pago no estará disponible.
                                </p>
                            </div>
                            
                            {/* Toggle Switch */}
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'space-between',
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: enablePayOption ? '#27ae60' : '#95a5a6' 
                                }}>
                                    {enablePayOption ? 'Pago Activado' : 'Pago Desactivado'}
                                </span>
                                <button
                                    onClick={toggleEnablePayOption}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: enablePayOption ? '#27ae60' : '#95a5a6',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        opacity: settingsLoading ? 0.6 : 1
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: enablePayOption ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                            </div>

                            {/* Price Input */}
                            <div style={{ 
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <label style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50',
                                    marginBottom: '8px',
                                    display: 'block'
                                }}>
                                    💰 Precio (ARS)
                                </label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        value={payOptionPrice}
                                        onChange={(e) => setPayOptionPrice(e.target.value)}
                                        min="0"
                                        step="100"
                                        style={{
                                            flex: 1,
                                            padding: isMobile ? '10px' : '12px',
                                            fontSize: isMobile ? '14px' : '15px',
                                            border: '2px solid #e1e8ed',
                                            borderRadius: '8px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                                    />
                                    <button
                                        onClick={updatePayOptionPrice}
                                        disabled={settingsLoading}
                                        style={{
                                            padding: isMobile ? '10px 16px' : '12px 20px',
                                            backgroundColor: '#667eea',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: isMobile ? '13px' : '14px',
                                            fontWeight: '600',
                                            cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                            opacity: settingsLoading ? 0.6 : 1,
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => !settingsLoading && (e.target.style.backgroundColor = '#5568d3')}
                                        onMouseLeave={(e) => !settingsLoading && (e.target.style.backgroundColor = '#667eea')}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>

                            {/* Discount Input */}
                            <div style={{ 
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px'
                            }}>
                                <label style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50',
                                    marginBottom: '8px',
                                    display: 'block'
                                }}>
                                    🎁 Descuento (%)
                                </label>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <input
                                        type="number"
                                        value={payOptionDiscountPercent}
                                        onChange={(e) => setPayOptionDiscountPercent(e.target.value)}
                                        min="0"
                                        max="100"
                                        step="5"
                                        style={{
                                            flex: 1,
                                            padding: isMobile ? '10px' : '12px',
                                            fontSize: isMobile ? '14px' : '15px',
                                            border: '2px solid #e1e8ed',
                                            borderRadius: '8px',
                                            outline: 'none',
                                            transition: 'border-color 0.2s'
                                        }}
                                        onFocus={(e) => e.target.style.borderColor = '#667eea'}
                                        onBlur={(e) => e.target.style.borderColor = '#e1e8ed'}
                                    />
                                    <button
                                        onClick={updatePayOptionDiscount}
                                        disabled={settingsLoading}
                                        style={{
                                            padding: isMobile ? '10px 16px' : '12px 20px',
                                            backgroundColor: '#667eea',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: isMobile ? '13px' : '14px',
                                            fontWeight: '600',
                                            cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                            opacity: settingsLoading ? 0.6 : 1,
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => !settingsLoading && (e.target.style.backgroundColor = '#5568d3')}
                                        onMouseLeave={(e) => !settingsLoading && (e.target.style.backgroundColor = '#667eea')}
                                    >
                                        Guardar
                                    </button>
                                </div>
                            </div>

                            {/* Free Tasacion Toggle */}
                            <div style={{ 
                                padding: isMobile ? '12px' : '15px',
                                backgroundColor: enableFreeTasacion ? '#e8f5e9' : '#ffe6e6',
                                borderRadius: '8px'
                            }}>
                                <span style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    fontWeight: '600', 
                                    color: enableFreeTasacion ? '#27ae60' : '#e74c3c' 
                                }}>
                                    {enableFreeTasacion ? '✅ Tasación Gratis Habilitada' : '⛔ Tasación Gratis Deshabilitada'}
                                </span>
                                <button
                                    onClick={toggleEnableFreeTasacion}
                                    disabled={settingsLoading}
                                    style={{
                                        position: 'relative',
                                        width: '60px',
                                        height: '32px',
                                        backgroundColor: enableFreeTasacion ? '#27ae60' : '#e74c3c',
                                        borderRadius: '16px',
                                        border: 'none',
                                        cursor: settingsLoading ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.3s ease',
                                        outline: 'none',
                                        marginLeft: '10px'
                                    }}
                                >
                                    <div style={{
                                        position: 'absolute',
                                        top: '4px',
                                        left: enableFreeTasacion ? '32px' : '4px',
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: 'white',
                                        borderRadius: '50%',
                                        transition: 'left 0.3s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}></div>
                                </button>
                                <div style={{
                                    marginTop: '10px',
                                    fontSize: isMobile ? '12px' : '13px',
                                    color: '#6c757d',
                                    lineHeight: '1.4'
                                }}>
                                    {enableFreeTasacion 
                                        ? '💡 Los usuarios pueden solicitar tasaciones gratuitas'
                                        : '⚠️ Las tasaciones gratuitas están deshabilitadas. Los usuarios solo verán la opción Premium.'}
                                </div>
                            </div>

                            {/* Display calculated final price */}
                            {payOptionDiscountPercent > 0 && (
                                <div style={{
                                    padding: isMobile ? '10px' : '12px',
                                    backgroundColor: '#e8f5e9',
                                    borderRadius: '8px',
                                    border: '1px solid #27ae60'
                                }}>
                                    <span style={{ 
                                        fontSize: isMobile ? '13px' : '14px', 
                                        color: '#27ae60',
                                        fontWeight: '600'
                                    }}>
                                        💵 Precio final con descuento: ${Math.round(payOptionPrice * (1 - payOptionDiscountPercent / 100)).toLocaleString('es-AR')}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Send Pending WhatsApp Messages Button */}
                    <div style={{ 
                        backgroundColor: 'white', 
                        borderRadius: isMobile ? '8px' : '12px', 
                        padding: isMobile ? '15px' : '20px', 
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        marginBottom: isMobile ? '15px' : '20px',
                        border: '2px solid #25D366'
                    }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column',
                            gap: '15px'
                        }}>
                            <div>
                                <h3 style={{ 
                                    fontSize: isMobile ? '16px' : '18px', 
                                    fontWeight: '600', 
                                    color: '#2c3e50', 
                                    marginBottom: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    📤 Enviar Mensajes de WhatsApp Pendientes
                                </h3>
                                <p style={{ 
                                    fontSize: isMobile ? '13px' : '14px', 
                                    color: '#7f8c8d', 
                                    marginBottom: '15px', 
                                    lineHeight: '1.6' 
                                }}>
                                    Fuerza el envío inmediato de todos los mensajes de WhatsApp que están en estado "pendiente" (fuera del horario de operación o en cola de envío).
                                    <br/><em style={{ fontSize: isMobile ? '12px' : '13px', color: '#95a5a6' }}>Útil para enviar mensajes acumulados sin esperar al cron job.</em>
                                </p>
                            </div>
                            <button
                                onClick={sendPendingWhatsAppMessages}
                                disabled={sendingPendingWhatsApp}
                                style={{
                                    padding: isMobile ? '12px 20px' : '14px 28px',
                                    backgroundColor: sendingPendingWhatsApp ? '#95a5a6' : '#25D366',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: isMobile ? '14px' : '16px',
                                    fontWeight: '600',
                                    cursor: sendingPendingWhatsApp ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 6px rgba(37, 211, 102, 0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    width: '100%',
                                    opacity: sendingPendingWhatsApp ? 0.6 : 1
                                }}
                                onMouseEnter={(e) => {
                                    if (!sendingPendingWhatsApp) {
                                        e.target.style.backgroundColor = '#128C7E';
                                        e.target.style.transform = 'translateY(-2px)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!sendingPendingWhatsApp) {
                                        e.target.style.backgroundColor = '#25D366';
                                        e.target.style.transform = 'translateY(0)';
                                    }
                                }}
                            >
                                {sendingPendingWhatsApp ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid white',
                                            borderTop: '2px solid transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }} />
                                        Enviando...
                                    </>
                                ) : (
                                    <>
                                        💬 Enviar Mensajes Pendientes
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Info box */}
                    <div style={{ 
                        backgroundColor: '#e8f4fd', 
                        border: '1px solid #3498db', 
                        borderRadius: isMobile ? '6px' : '8px', 
                        padding: isMobile ? '12px' : '15px',
                        fontSize: isMobile ? '13px' : '14px',
                        color: '#2c3e50',
                        lineHeight: '1.5'
                    }}>
                        <strong>ℹ️ Nota:</strong> Los cambios en esta configuración se aplican inmediatamente a todas las nuevas tasaciones.
                    </div>
                </div>
            )}

            {/* Hide Actualizar Métricas button on messages page */}
            {activeTab !== 'messages' && (
                <div style={{ 
                    textAlign: 'center', 
                    marginTop: isMobile ? '20px' : '30px',
                    paddingBottom: isMobile ? '20px' : '0'
                }}>
                    <button 
                        onClick={() => fetchMetrics(1)} 
                        style={{ 
                            padding: isMobile ? '14px' : '12px 24px',
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '300px' : 'none',
                            backgroundColor: '#667eea', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '10px', 
                            cursor: 'pointer', 
                            fontSize: isMobile ? '15px' : '16px', 
                            fontWeight: '600', 
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                        }}
                    >
                        🔄 Actualizar Métricas
                    </button>
                </div>
            )}

            {/* Tasacion Detail Modal */}
            {showTasacionModal && selectedTasacionDetail && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 10000,
                    padding: '20px',
                    overflowY: 'auto'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: isMobile ? '25px 20px' : '35px 40px',
                        maxWidth: '700px',
                        width: '100%',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.3)',
                        position: 'relative'
                    }}>
                        {/* Close Button */}
                        <button
                            onClick={closeTasacionModal}
                            style={{
                                position: 'absolute',
                                top: '15px',
                                right: '15px',
                                background: '#f8f9fa',
                                border: 'none',
                                borderRadius: '50%',
                                width: '35px',
                                height: '35px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = '#e9ecef';
                                e.target.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = '#f8f9fa';
                                e.target.style.transform = 'scale(1)';
                            }}
                        >
                            ✕
                        </button>

                        {/* Header */}
                        <div style={{
                            marginBottom: '25px',
                            paddingBottom: '20px',
                            borderBottom: '2px solid #e9ecef'
                        }}>
                            <h2 style={{
                                margin: '0 0 8px 0',
                                fontSize: isMobile ? '1.4rem' : '1.7rem',
                                fontWeight: '700',
                                color: '#2c3e50'
                            }}>
                                📋 Detalles de Tasación
                            </h2>
                            <p style={{
                                margin: 0,
                                fontSize: '0.9rem',
                                color: '#7f8c8d'
                            }}>
                                {selectedTasacionDetail.timestamp ? new Date(selectedTasacionDetail.timestamp).toLocaleDateString('es-AR', { 
                                    year: 'numeric',
                                    month: 'long', 
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                }) : 'N/A'}
                            </p>
                        </div>

                        {/* Content */}
                        <div style={{ marginBottom: '25px' }}>
                            {/* Address */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#667eea',
                                    marginBottom: '5px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    📍 Dirección
                                </label>
                                <p style={{ 
                                    margin: 0,
                                    fontSize: '1rem',
                                    color: '#2c3e50',
                                    fontWeight: '500'
                                }}>
                                    {selectedTasacionDetail.address || 'N/A'}
                                </p>
                            </div>

                            {/* Zone */}
                            {selectedTasacionDetail.zone && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        🏘️ Zona
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>
                                        {selectedTasacionDetail.zone}
                                    </p>
                                </div>
                            )}

                            {/* Property Details Row */}
                            <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                                gap: '20px',
                                marginBottom: '20px'
                            }}>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        🏠 Tipo de Propiedad
                                    </label>
                                    <p style={{ margin: 0, fontSize: '1rem', color: '#2c3e50' }}>
                                        {selectedTasacionDetail.property || 'N/A'}
                                    </p>
                                </div>
                                <div>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        💼 Operación
                                    </label>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '6px 14px',
                                        borderRadius: '20px',
                                        fontSize: '0.9rem',
                                        fontWeight: 'bold',
                                        backgroundColor: selectedTasacionDetail.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                        color: selectedTasacionDetail.operation === 'alquiler' ? '#1976d2' : '#7b1fa2'
                                    }}>
                                        {selectedTasacionDetail.operation === 'alquiler' ? 'Alquiler' : 'Venta'}
                                    </span>
                                </div>
                            </div>

                            {/* Property Characteristics */}
                            {(selectedTasacionDetail.amb || selectedTasacionDetail.sc || selectedTasacionDetail.st || selectedTasacionDetail.garage) && (
                                <div style={{
                                    background: '#f8f9fa',
                                    padding: '15px',
                                    borderRadius: '10px',
                                    marginBottom: '20px'
                                }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '10px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        📐 Características
                                    </label>
                                    <div style={{ 
                                        display: 'grid',
                                        gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr',
                                        gap: '10px'
                                    }}>
                                        {selectedTasacionDetail.amb && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Ambientes:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.amb}
                                                </p>
                                            </div>
                                        )}
                                        {selectedTasacionDetail.sc && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Sup. Cubierta:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.sc}m²
                                                </p>
                                            </div>
                                        )}
                                        {selectedTasacionDetail.st && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Sup. Total:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.st}m²
                                                </p>
                                            </div>
                                        )}
                                        {selectedTasacionDetail.garage && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Cochera:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.garage}
                                                </p>
                                            </div>
                                        )}
                                        {selectedTasacionDetail.antiguedad && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Antigüedad:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.antiguedad} años
                                                </p>
                                            </div>
                                        )}
                                        {selectedTasacionDetail.condition && (
                                            <div>
                                                <span style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>Estado:</span>
                                                <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                    {selectedTasacionDetail.condition}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Price Range */}
                            {selectedTasacionDetail.priceRange && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        💰 Valuación
                                    </label>
                                    <p style={{ 
                                        margin: 0,
                                        fontSize: '1.1rem',
                                        color: '#27ae60',
                                        fontWeight: 'bold'
                                    }}>
                                        {selectedTasacionDetail.priceRange}
                                    </p>
                                </div>
                            )}

                            {/* Contact Info */}
                            <div style={{
                                background: '#e8f5e9',
                                padding: '15px',
                                borderRadius: '10px',
                                marginBottom: '20px'
                            }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.85rem',
                                    fontWeight: '600',
                                    color: '#27ae60',
                                    marginBottom: '10px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    👤 Contacto
                                </label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {selectedTasacionDetail.phone && (
                                        <div>
                                            <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>📞 Teléfono:</span>
                                            <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                {selectedTasacionDetail.phone}
                                            </p>
                                        </div>
                                    )}
                                    {selectedTasacionDetail.email && (
                                        <div>
                                            <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>📧 Email:</span>
                                            <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                {selectedTasacionDetail.email}
                                            </p>
                                        </div>
                                    )}
                                    {selectedTasacionDetail.motive && (
                                        <div>
                                            <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>💡 Motivo:</span>
                                            <p style={{ margin: '2px 0 0 0', fontWeight: '600', color: '#2c3e50' }}>
                                                {selectedTasacionDetail.motive}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* IP Address */}
                            {selectedTasacionDetail.ip && (
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={{ 
                                        display: 'block',
                                        fontSize: '0.85rem',
                                        fontWeight: '600',
                                        color: '#667eea',
                                        marginBottom: '5px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        🌐 Dirección IP
                                    </label>
                                    <p style={{ 
                                        margin: 0,
                                        fontSize: '0.9rem',
                                        color: '#7f8c8d',
                                        fontFamily: 'monospace'
                                    }}>
                                        {selectedTasacionDetail.ip}
                                    </p>
                                </div>
                            )}

                            {/* Appointment Scheduling Section */}
                            <div style={{
                                background: 'linear-gradient(135deg, #fef5e7 0%, #fff9e6 100%)',
                                padding: '20px',
                                borderRadius: '12px',
                                marginBottom: '20px',
                                border: '2px solid #f9ca24',
                                boxShadow: '0 2px 8px rgba(249, 202, 36, 0.15)'
                            }}>
                                <label style={{ 
                                    display: 'block',
                                    fontSize: '0.95rem',
                                    fontWeight: '700',
                                    color: '#f39c12',
                                    marginBottom: '15px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    📅 Agendar Cita con Martillero
                                </label>

                                {/* Tasacion Details for Appointment */}
                                <div style={{
                                    background: 'white',
                                    padding: '15px',
                                    borderRadius: '8px',
                                    marginBottom: '15px',
                                    border: '1px solid #f0f0f0'
                                }}>
                                    <div style={{ 
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        color: '#7f8c8d',
                                        marginBottom: '10px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px'
                                    }}>
                                        Detalles de la Propiedad
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <span style={{ fontSize: '0.85rem', color: '#7f8c8d', minWidth: '80px' }}>📍 Dirección:</span>
                                            <span style={{ fontSize: '0.85rem', color: '#2c3e50', fontWeight: '600' }}>
                                                {selectedTasacionDetail.address}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <span style={{ fontSize: '0.85rem', color: '#7f8c8d', minWidth: '80px' }}>📞 Teléfono:</span>
                                            <span style={{ fontSize: '0.85rem', color: '#2c3e50', fontWeight: '600' }}>
                                                {selectedTasacionDetail.phone}
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <span style={{ fontSize: '0.85rem', color: '#7f8c8d', minWidth: '80px' }}>💰 Valuación:</span>
                                            <span style={{ fontSize: '0.85rem', color: '#27ae60', fontWeight: '700' }}>
                                                {selectedTasacionDetail.priceRange}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div>
                                        <label style={{ 
                                            display: 'block',
                                            fontSize: '0.85rem',
                                            fontWeight: '600',
                                            color: '#2c3e50',
                                            marginBottom: '5px'
                                        }}>
                                            Nombre del Cliente
                                        </label>
                                        <input
                                            type="text"
                                            value={appointmentName}
                                            onChange={(e) => setAppointmentName(e.target.value)}
                                            placeholder="Ej: Juan Pérez"
                                            style={{
                                                width: '100%',
                                                padding: '10px 12px',
                                                border: '2px solid #e0e0e0',
                                                borderRadius: '8px',
                                                fontSize: '0.95rem',
                                                outline: 'none',
                                                transition: 'border-color 0.2s',
                                                boxSizing: 'border-box'
                                            }}
                                            onFocus={(e) => e.target.style.borderColor = '#f39c12'}
                                            onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div>
                                            <label style={{ 
                                                display: 'block',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#2c3e50',
                                                marginBottom: '5px'
                                            }}>
                                                Fecha
                                            </label>
                                            <input
                                                type="date"
                                                value={appointmentDate}
                                                onChange={(e) => setAppointmentDate(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    border: '2px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.2s',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#f39c12'}
                                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                            />
                                        </div>

                                        <div>
                                            <label style={{ 
                                                display: 'block',
                                                fontSize: '0.85rem',
                                                fontWeight: '600',
                                                color: '#2c3e50',
                                                marginBottom: '5px'
                                            }}>
                                                Hora
                                            </label>
                                            <input
                                                type="time"
                                                value={appointmentTime}
                                                onChange={(e) => setAppointmentTime(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 12px',
                                                    border: '2px solid #e0e0e0',
                                                    borderRadius: '8px',
                                                    fontSize: '0.95rem',
                                                    outline: 'none',
                                                    transition: 'border-color 0.2s',
                                                    boxSizing: 'border-box'
                                                }}
                                                onFocus={(e) => e.target.style.borderColor = '#f39c12'}
                                                onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                            />
                                        </div>
                                    </div>

                                    <button
                                        onClick={saveAppointment}
                                        disabled={savingAppointment || !appointmentDate || !appointmentTime || !appointmentName}
                                        style={{
                                            width: '100%',
                                            padding: '12px',
                                            background: (savingAppointment || !appointmentDate || !appointmentTime || !appointmentName) 
                                                ? '#bdc3c7' 
                                                : 'linear-gradient(135deg, #f39c12, #e67e22)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '8px',
                                            fontSize: '0.95rem',
                                            fontWeight: '700',
                                            cursor: (savingAppointment || !appointmentDate || !appointmentTime || !appointmentName) 
                                                ? 'not-allowed' 
                                                : 'pointer',
                                            transition: 'all 0.2s',
                                            marginTop: '8px'
                                        }}
                                    >
                                        {savingAppointment ? '⏳ Guardando...' : '💾 Guardar Cita'}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{ 
                            display: 'flex',
                            gap: '12px',
                            paddingTop: '20px',
                            borderTop: '2px solid #e9ecef'
                        }}>
                            <button
                                onClick={() => copyTasacionToClipboard(selectedTasacionDetail)}
                                style={{
                                    flex: 1,
                                    padding: '14px 24px',
                                    background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '10px',
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.transform = 'translateY(-2px)';
                                    e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.transform = 'translateY(0)';
                                    e.target.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.3)';
                                }}
                            >
                                📋 Copiar Datos
                            </button>
                            <button
                                onClick={closeTasacionModal}
                                style={{
                                    padding: '14px 24px',
                                    background: 'white',
                                    color: '#667eea',
                                    border: '2px solid #667eea',
                                    borderRadius: '10px',
                                    fontSize: '1rem',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.target.style.background = '#f8f9fa';
                                }}
                                onMouseLeave={(e) => {
                                    e.target.style.background = 'white';
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Metrics; 